import React, { useState, useEffect, useCallback } from "react";
import SiraguLogo from "./SiraguLogo";
import { authHeaders } from "../auth";
import "./AiConfigPage.css";

const API_BASE = process.env.REACT_APP_API_BASE;

/* ── Empty-block factories ────────────────────────────────────────────────
   Every JSONB block is optional; empty/blank fields are pruned before POST so
   the backend inherits the default. These give the editors a stable shape.
   ─────────────────────────────────────────────────────────────────────── */
function emptyTier() {
  return { provider: "", model: "", endpoint: "", api_key_ref: "", enabled: false };
}

function emptyProviderRow() {
  return {
    provider: "",
    models: "",
    endpoint: "",
    endpoint_ref: "",
    api_key_ref: "",
    timeout: "",
  };
}

function emptyMaskingRow() {
  return {
    columns: [],
    mode: "redact",
    show_last: "",
    except_roles: [],
    except_perms: [],
  };
}

function emptyRuleRow() {
  return {
    effect: "deny",
    when_role: "",
    when_permission: "",
    capabilities: [],
    topics: [],
  };
}

function emptySecurity() {
  return {
    institution_read_perms: [],
    self_service_enabled: false,
    block_llm_sql: false,
    extra_sensitive_columns: [],
    allow_columns: [],
    masking: [],
    rules: [],
  };
}

/* Public-visitor (unauthenticated admissions assistant) config. */
function emptyVisitor() {
  return {
    enabled: false,
    accessible_source_types: [],
    accessible_source_ids: [],
    highlights: "",
    persona_prompt: "",
  };
}

function blankForm() {
  return {
    id: null,
    name: "",
    description: "",
    institution_id: "",
    app_id: "",
    provider_priority: [],
    prompts: { system: "", medium: "", minimal: "", executive_addendum: "", rag: "", analytics: "", tool_picker: "" },
    enabled_capabilities: null, // null = all
    guardrails: {
      max_rows: "",
      query_timeout_seconds: "",
      temperature: "",
      max_tokens: "",
      allowed_schemas: "",
    },
    llm_tiers: { small: emptyTier(), large: emptyTier(), premium: emptyTier() },
    confidence: { threshold: 0.6, premium_enabled: false },
    embeddings: {
      enabled: false,
      provider: "",
      model: "",
      endpoint: "",
      endpoint_ref: "",
      api_key_ref: "",
      dimensions: "",
    },
    features: {
      faq_enabled: false,
      rag_enabled: false,
      cache_enabled: false,
      cache_ttl_seconds: "",
    },
    security: emptySecurity(),
    visitor: emptyVisitor(),
    is_active: false,
  };
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const csvToArr = (s) =>
  (s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
const arrToCsv = (a) => (Array.isArray(a) ? a.join(", ") : "");

// Collapse an object to null when every value is blank/empty.
function pruneEmpty(obj) {
  const out = {};
  let any = false;
  Object.entries(obj).forEach(([k, v]) => {
    if (v === "" || v === null || v === undefined) return;
    if (Array.isArray(v) && v.length === 0) return;
    out[k] = v;
    any = true;
  });
  return any ? out : null;
}

/* Build the POST/PUT payload from the form, pruning blank blocks to null.
   `promptDefaults` (optional) lets us send null for prompt fields that still
   equal the built-in default, so we never persist the huge system prompt when
   it hasn't actually been overridden. */
function buildPayload(form, promptDefaults) {
  const tier = (t) => {
    const p = pruneEmpty({
      provider: t.provider,
      model: t.model,
      endpoint: t.endpoint,
      api_key_ref: t.api_key_ref,
      enabled: t.enabled ? true : null,
    });
    return p;
  };

  const providers = (form.provider_priority || [])
    .map((r) => {
      const p = pruneEmpty({
        provider: r.provider,
        models: csvToArr(r.models),
        endpoint: r.endpoint,
        endpoint_ref: r.endpoint_ref,
        api_key_ref: r.api_key_ref,
        timeout: num(r.timeout),
      });
      return p;
    })
    .filter(Boolean);

  // For each prompt slot, only send an OVERRIDE when the current text differs
  // from the built-in default; if it equals the default (or is blank), send
  // null so the config keeps tracking the code default.
  const PROMPT_KEYS = [
    "system",
    "medium",
    "minimal",
    "executive_addendum",
    "rag",
    "analytics",
    "tool_picker",
  ];
  const defs = promptDefaults || {};
  const norm = (s) => (s == null ? "" : String(s));
  const promptEntries = {};
  let anyPromptOverride = false;
  PROMPT_KEYS.forEach((k) => {
    const cur = norm(form.prompts[k]);
    const def = norm(defs[k]);
    // Blank, or unchanged from default → null (inherit code default). We keep
    // the explicit null (rather than pruning it) so an override that was reset
    // back to default is cleared on the server instead of silently retained.
    if (cur === "" || cur === def) {
      promptEntries[k] = null;
    } else {
      promptEntries[k] = cur;
      anyPromptOverride = true;
    }
  });
  const prompts = anyPromptOverride ? promptEntries : null;

  const guardrails = pruneEmpty({
    max_rows: num(form.guardrails.max_rows),
    query_timeout_seconds: num(form.guardrails.query_timeout_seconds),
    temperature: num(form.guardrails.temperature),
    max_tokens: num(form.guardrails.max_tokens),
    allowed_schemas: csvToArr(form.guardrails.allowed_schemas),
  });

  const llmTiers = pruneEmpty({
    small: tier(form.llm_tiers.small),
    large: tier(form.llm_tiers.large),
    premium: tier(form.llm_tiers.premium),
  });

  const embeddings = form.embeddings.enabled
    ? pruneEmpty({
        enabled: true,
        provider: form.embeddings.provider,
        model: form.embeddings.model,
        endpoint: form.embeddings.endpoint,
        endpoint_ref: form.embeddings.endpoint_ref,
        api_key_ref: form.embeddings.api_key_ref,
        dimensions: num(form.embeddings.dimensions),
      })
    : { enabled: false };

  const features = pruneEmpty({
    faq_enabled: form.features.faq_enabled ? true : null,
    rag_enabled: form.features.rag_enabled ? true : null,
    cache_enabled: form.features.cache_enabled ? true : null,
    cache_ttl_seconds: num(form.features.cache_ttl_seconds),
  });

  const confidence = pruneEmpty({
    threshold: num(form.confidence.threshold),
    premium_enabled: form.confidence.premium_enabled ? true : null,
  });

  const security = buildSecurity(form.security);
  const visitor = buildVisitor(form.visitor);

  return {
    name: form.name,
    description: form.description || null,
    institution_id: form.institution_id || null,
    app_id: form.app_id || null,
    provider_priority: providers.length ? providers : null,
    prompts,
    enabled_capabilities: form.enabled_capabilities, // null = all
    guardrails,
    llm_tiers: llmTiers,
    confidence,
    embeddings,
    features,
    security,
    visitor,
    is_active: !!form.is_active,
  };
}

/* Assemble the public-visitor block. `highlights`/`persona_prompt` are sent as
   null when blank so the config inherits the built-in admissions defaults. */
function buildVisitor(vis) {
  if (!vis) return null;
  const types = (vis.accessible_source_types || []).filter(Boolean);
  const ids = (vis.accessible_source_ids || []).filter(Boolean);
  const highlights = (vis.highlights || "").trim();
  const persona = (vis.persona_prompt || "").trim();
  return {
    enabled: !!vis.enabled,
    accessible_source_types: types,
    accessible_source_ids: ids,
    highlights: highlights || null,
    persona_prompt: persona || null,
  };
}

/* Assemble the security block, pruning empty rows/fields before sending. */
function buildSecurity(sec) {
  if (!sec) return null;
  const s = sec;

  // Masking rows: keep only rows that target at least one column.
  const masking = (s.masking || [])
    .map((r) => {
      const cols = Array.isArray(r.columns) ? r.columns.filter(Boolean) : [];
      if (cols.length === 0) return null;
      const row = {
        columns: cols,
        mode: r.mode || "redact",
      };
      if (r.mode === "partial") {
        const sl = num(r.show_last);
        if (sl !== null) row.show_last = sl;
      }
      const exRoles = (r.except_roles || []).filter(Boolean);
      const exPerms = (r.except_perms || []).filter(Boolean);
      if (exRoles.length) row.except_roles = exRoles;
      if (exPerms.length) row.except_perms = exPerms;
      return row;
    })
    .filter(Boolean);

  // Access rules: keep rows with an effect and at least one target dimension.
  const rules = (s.rules || [])
    .map((r) => {
      const caps = (r.capabilities || []).filter(Boolean);
      const topics = (r.topics || []).filter(Boolean);
      const whenRole = (r.when_role || "").trim();
      const whenPerm = (r.when_permission || "").trim();
      if (!whenRole && !whenPerm && caps.length === 0 && topics.length === 0) return null;
      const row = { effect: r.effect || "deny" };
      row.when_role = whenRole || null;
      row.when_permission = whenPerm || null;
      row.capabilities = caps;
      row.topics = topics;
      return row;
    })
    .filter(Boolean);

  const instPerms = (s.institution_read_perms || []).filter(Boolean);
  const extraCols = (s.extra_sensitive_columns || []).filter(Boolean);
  const allowCols = (s.allow_columns || []).filter(Boolean);

  const out = {
    institution_read_perms: instPerms.length ? instPerms : null,
    self_service_enabled: !!s.self_service_enabled,
    block_llm_sql: !!s.block_llm_sql,
    extra_sensitive_columns: extraCols,
    allow_columns: allowCols,
    masking,
    rules,
  };
  return out;
}

/* Hydrate the editable form from a full config row fetched from the API. */
function formFromRow(row) {
  const base = blankForm();
  if (!row) return base;
  base.id = row.id ?? null;
  base.name = row.name ?? "";
  base.description = row.description ?? "";
  base.institution_id = row.institution_id ?? "";
  base.app_id = row.app_id ?? "";
  base.is_active = !!row.is_active;

  if (Array.isArray(row.provider_priority)) {
    base.provider_priority = row.provider_priority.map((r) => ({
      provider: r.provider ?? "",
      models: arrToCsv(r.models),
      endpoint: r.endpoint ?? "",
      endpoint_ref: r.endpoint_ref ?? "",
      api_key_ref: r.api_key_ref ?? "",
      timeout: r.timeout ?? "",
    }));
  }

  if (row.prompts) {
    base.prompts = {
      system: row.prompts.system ?? "",
      medium: row.prompts.medium ?? "",
      minimal: row.prompts.minimal ?? "",
      executive_addendum: row.prompts.executive_addendum ?? "",
      rag: row.prompts.rag ?? "",
      analytics: row.prompts.analytics ?? "",
      tool_picker: row.prompts.tool_picker ?? "",
    };
  }

  base.enabled_capabilities = Array.isArray(row.enabled_capabilities)
    ? row.enabled_capabilities
    : null;

  if (row.guardrails) {
    base.guardrails = {
      max_rows: row.guardrails.max_rows ?? "",
      query_timeout_seconds: row.guardrails.query_timeout_seconds ?? "",
      temperature: row.guardrails.temperature ?? "",
      max_tokens: row.guardrails.max_tokens ?? "",
      allowed_schemas: arrToCsv(row.guardrails.allowed_schemas),
    };
  }

  const hydrateTier = (t) =>
    t
      ? {
          provider: t.provider ?? "",
          model: t.model ?? "",
          endpoint: t.endpoint ?? "",
          api_key_ref: t.api_key_ref ?? "",
          enabled: !!t.enabled,
        }
      : emptyTier();
  if (row.llm_tiers) {
    base.llm_tiers = {
      small: hydrateTier(row.llm_tiers.small),
      large: hydrateTier(row.llm_tiers.large),
      premium: hydrateTier(row.llm_tiers.premium),
    };
  }

  if (row.confidence) {
    base.confidence = {
      threshold: row.confidence.threshold ?? 0.6,
      premium_enabled: !!row.confidence.premium_enabled,
    };
  }

  if (row.embeddings) {
    base.embeddings = {
      enabled: !!row.embeddings.enabled,
      provider: row.embeddings.provider ?? "",
      model: row.embeddings.model ?? "",
      endpoint: row.embeddings.endpoint ?? "",
      endpoint_ref: row.embeddings.endpoint_ref ?? "",
      api_key_ref: row.embeddings.api_key_ref ?? "",
      dimensions: row.embeddings.dimensions ?? "",
    };
  }

  if (row.features) {
    base.features = {
      faq_enabled: !!row.features.faq_enabled,
      rag_enabled: !!row.features.rag_enabled,
      cache_enabled: !!row.features.cache_enabled,
      cache_ttl_seconds: row.features.cache_ttl_seconds ?? "",
    };
  }

  base.security = securityFromRow(row.security);
  base.visitor = visitorFromRow(row.visitor);

  return base;
}

/* Hydrate the editable visitor block from a config's `visitor` object. */
function visitorFromRow(vis) {
  const base = emptyVisitor();
  if (!vis) return base;
  base.enabled = !!vis.enabled;
  base.accessible_source_types = Array.isArray(vis.accessible_source_types)
    ? vis.accessible_source_types
    : [];
  base.accessible_source_ids = Array.isArray(vis.accessible_source_ids)
    ? vis.accessible_source_ids
    : [];
  base.highlights = vis.highlights ?? "";
  base.persona_prompt = vis.persona_prompt ?? "";
  return base;
}

/* Hydrate the editable security block from a config's `security` object. */
function securityFromRow(sec) {
  const base = emptySecurity();
  if (!sec) return base;
  base.institution_read_perms = Array.isArray(sec.institution_read_perms)
    ? sec.institution_read_perms
    : [];
  base.self_service_enabled = !!sec.self_service_enabled;
  base.block_llm_sql = !!sec.block_llm_sql;
  base.extra_sensitive_columns = Array.isArray(sec.extra_sensitive_columns)
    ? sec.extra_sensitive_columns
    : [];
  base.allow_columns = Array.isArray(sec.allow_columns) ? sec.allow_columns : [];
  base.masking = Array.isArray(sec.masking)
    ? sec.masking.map((m) => ({
        columns: Array.isArray(m.columns) ? m.columns : [],
        mode: m.mode || "redact",
        show_last: m.show_last ?? "",
        except_roles: Array.isArray(m.except_roles) ? m.except_roles : [],
        except_perms: Array.isArray(m.except_perms) ? m.except_perms : [],
      }))
    : [];
  base.rules = Array.isArray(sec.rules)
    ? sec.rules.map((r) => ({
        effect: r.effect || "deny",
        when_role: r.when_role ?? "",
        when_permission: r.when_permission ?? "",
        capabilities: Array.isArray(r.capabilities) ? r.capabilities : [],
        topics: Array.isArray(r.topics) ? r.topics : [],
      }))
    : [];
  return base;
}

function scopeLabel(row) {
  if (row.institution_id && row.app_id) return "Institution + App";
  if (row.institution_id) return "Institution";
  return "Global default";
}

/* ── Small presentational pieces ─────────────────────────────────────────── */
function Toggle({ checked, onChange, label }) {
  return (
    <label className="cfg-toggle">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="cfg-toggle-track" aria-hidden="true" />
      {label && <span className="cfg-toggle-label">{label}</span>}
    </label>
  );
}

function Section({ id, title, subtitle, open, onToggle, children }) {
  return (
    <div className="cfg-section">
      <button className="cfg-section-head" onClick={() => onToggle(id)} type="button">
        <div>
          <span className="cfg-section-title">{title}</span>
          {subtitle && <span className="cfg-section-sub">{subtitle}</span>}
        </div>
        <span className={`cfg-chevron ${open ? "open" : ""}`}>&#8250;</span>
      </button>
      {open && <div className="cfg-section-body">{children}</div>}
    </div>
  );
}

function HelperNote({ children }) {
  return <p className="cfg-helper">{children}</p>;
}

/* Tag / chip input: type a value and press Enter or comma to add a chip.
   `value` is an array of strings; `onChange` receives the next array. */
function TagInput({ value, onChange, placeholder }) {
  const [draft, setDraft] = useState("");
  const tags = Array.isArray(value) ? value : [];

  const commit = (raw) => {
    const parts = String(raw)
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const next = [...tags];
    parts.forEach((p) => {
      if (!next.includes(p)) next.push(p);
    });
    onChange(next);
    setDraft("");
  };

  const removeTag = (i) => onChange(tags.filter((_, idx) => idx !== i));

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && !draft && tags.length) {
      removeTag(tags.length - 1);
    }
  };

  return (
    <div className="cfg-taginput">
      {tags.map((t, i) => (
        <span key={`${t}-${i}`} className="cfg-tag">
          {t}
          <button
            type="button"
            className="cfg-tag-x"
            onClick={() => removeTag(i)}
            aria-label={`Remove ${t}`}
          >
            &#215;
          </button>
        </span>
      ))}
      <input
        className="cfg-tag-field"
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => draft && commit(draft)}
        placeholder={tags.length === 0 ? placeholder : ""}
      />
    </div>
  );
}

/* ── Tab icons (line SVG, 18px, currentColor) ────────────────────────────── */
function TabIcon({ name }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };
  switch (name) {
    case "overview":
      return (
        <svg {...common}><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /></svg>
      );
    case "models":
      return (
        <svg {...common}><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5" /><path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" /></svg>
      );
    case "tools":
      return (
        <svg {...common}><path d="M14.7 6.3a4 4 0 00-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 005.4-5.4l-2.6 2.6-2.4-.6-.6-2.4z" /></svg>
      );
    case "prompts":
      return (
        <svg {...common}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" /></svg>
      );
    case "knowledge":
      return (
        <svg {...common}><path d="M4 5a2 2 0 012-2h9a2 2 0 012 2v14a1 1 0 00-1-1H6a2 2 0 00-2 2z" /><path d="M18 3h1a1 1 0 011 1v15" /><path d="M8 7h6M8 11h6" /></svg>
      );
    case "configuration":
      return (
        <svg {...common}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.6 1.6 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.6 1.6 0 00-2.8 1.1V21a2 2 0 11-4 0v-.1a1.6 1.6 0 00-2.8-1.1l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.6 1.6 0 00-1.1-2.8H3a2 2 0 110-4h.1a1.6 1.6 0 001.1-2.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.6 1.6 0 002.8-1.1V3a2 2 0 114 0v.1a1.6 1.6 0 002.8 1.1l.1-.1a2 2 0 112.8 2.8l-.1.1a1.6 1.6 0 001.1 2.8H21a2 2 0 110 4h-.1a1.6 1.6 0 00-1.5 1z" /></svg>
      );
    case "logs":
      return (
        <svg {...common}><path d="M3 5h18M3 12h18M3 19h18" /></svg>
      );
    case "security":
      return (
        <svg {...common}><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" /><path d="M9 12l2 2 4-4" /></svg>
      );
    case "visitor":
      return (
        <svg {...common}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>
      );
    case "feedback":
      return (
        <svg {...common}><path d="M7 10v11" /><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88z" /></svg>
      );
    default:
      return null;
  }
}

/* ── Tab definitions ─────────────────────────────────────────────────────── */
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "models", label: "Models" },
  { id: "tools", label: "Tools" },
  { id: "prompts", label: "Prompts" },
  { id: "knowledge", label: "Knowledge Base" },
  { id: "configuration", label: "Configuration" },
  { id: "logs", label: "Logs" },
  { id: "security", label: "Security" },
  { id: "visitor", label: "Visitor Access" },
  { id: "feedback", label: "Feedback" },
];

const TAB_META = {
  overview: { title: "Overview", desc: "Live status of the Siragu AI engine, pipeline and model tiers." },
  models: { title: "Models", desc: "Configure the LLM tiers, providers and confidence escalation." },
  tools: { title: "Tools", desc: "Enable the capabilities Siragu can call to answer questions." },
  prompts: { title: "Prompts", desc: "Override the system and complexity-tier prompts." },
  knowledge: { title: "Knowledge Base", desc: "Manage the data sources, websites and documents Siragu learns from." },
  configuration: { title: "Configuration", desc: "Create, scope, publish and activate configuration profiles." },
  logs: { title: "Logs", desc: "Recent message activity, intents and feedback." },
  security: { title: "Security", desc: "Data-privacy posture and safeguards." },
  visitor: { title: "Visitor Access", desc: "The public admissions assistant shown to unauthenticated visitors." },
  feedback: { title: "Feedback", desc: "Disliked answers and the learned corrections applied going forward." },
};

/* ── Main page ───────────────────────────────────────────────────────────── */
function AiConfigPage({ onBack }) {
  const [tab, setTab] = useState("overview");

  const [meta, setMeta] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [promptDefaults, setPromptDefaults] = useState(null);
  const [promptDefaultsLoading, setPromptDefaultsLoading] = useState(true);
  const [promptDefaultsError, setPromptDefaultsError] = useState(null);
  const [promptsHydrated, setPromptsHydrated] = useState(false);
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const [logs, setLogs] = useState([]);
  const [logsError, setLogsError] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);

  // Feedback (disliked answers + learned corrections)
  const [feedback, setFeedback] = useState(null); // null = not loaded yet
  const [feedbackError, setFeedbackError] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState("all");

  const [configs, setConfigs] = useState([]);
  const [listError, setListError] = useState(null);
  const [listLoading, setListLoading] = useState(true);

  // Resolved (effective) preview
  const [scope, setScope] = useState({ institution_id: "", app_id: "" });
  const [resolved, setResolved] = useState(null);
  const [resolvedLoading, setResolvedLoading] = useState(false);
  const [resolvedError, setResolvedError] = useState(null);

  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // {type, msg}

  const [openSections, setOpenSections] = useState({
    tiers: true,
    providers: false,
    prompts: false,
    capabilities: false,
    guardrails: false,
    confidence: false,
    embeddings: false,
    features: false,
  });

  // Test panel
  const [testQ, setTestQ] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState(null);

  // Knowledge base
  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesError, setSourcesError] = useState(null);
  const [discovered, setDiscovered] = useState(null);
  const [discoveredLoading, setDiscoveredLoading] = useState(false);
  const [discoveredError, setDiscoveredError] = useState(null);

  const loadSources = useCallback(async () => {
    setSourcesLoading(true);
    setSourcesError(null);
    try {
      const res = await fetch(`${API_BASE}/knowledge-sources`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Server error: ${res.status}`);
      }
      const data = await res.json();
      setSources(Array.isArray(data.sources) ? data.sources : []);
    } catch (err) {
      setSourcesError(err.message);
      setSources([]);
    } finally {
      setSourcesLoading(false);
    }
  }, []);

  const loadDiscovered = useCallback(async () => {
    setDiscoveredLoading(true);
    setDiscoveredError(null);
    try {
      const res = await fetch(`${API_BASE}/knowledge-sources/tools`, {
        headers: { ...authHeaders() },
      });
      if (res.ok) {
        setDiscovered(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        setDiscovered(null);
        setDiscoveredError(err.detail || err.message || `Server error: ${res.status}`);
      }
    } catch (err) {
      setDiscovered(null);
      setDiscoveredError(err.message || "Failed to load discovered tools.");
    } finally {
      setDiscoveredLoading(false);
    }
  }, []);

  // Lazy-load the knowledge base the first time the tab is opened.
  useEffect(() => {
    if (tab === "knowledge") {
      loadSources();
      loadDiscovered();
    }
  }, [tab, loadSources, loadDiscovered]);

  // Lazy-load discovered tools the first time the Tools tab is opened.
  useEffect(() => {
    if (tab === "tools" && discovered === null && !discoveredLoading && !discoveredError) {
      loadDiscovered();
    }
  }, [tab, discovered, discoveredLoading, discoveredError, loadDiscovered]);

  const toggleSection = (id) => setOpenSections((s) => ({ ...s, [id]: !s[id] }));
  const flash = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  /* ── Fetch: config list ────────────────────────────────────────────────── */
  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await fetch(`${API_BASE}/ai-config`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Server error: ${res.status}`);
      }
      const data = await res.json();
      setConfigs(Array.isArray(data.configs) ? data.configs : []);
    } catch (err) {
      setListError(err.message);
      setConfigs([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  /* ── Fetch: dashboard overview ─────────────────────────────────────────── */
  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const res = await fetch(`${API_BASE}/ai-config/overview`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Server error: ${res.status}`);
      }
      setOverview(await res.json());
    } catch (err) {
      setOverviewError(err.message);
      setOverview(null);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  /* ── Fetch: logs ───────────────────────────────────────────────────────── */
  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const res = await fetch(`${API_BASE}/ai-config/logs?limit=100`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Server error: ${res.status}`);
      }
      const data = await res.json();
      setLogs(Array.isArray(data.logs) ? data.logs : []);
    } catch (err) {
      setLogsError(err.message);
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  /* ── Fetch: feedback (disliked answers) ────────────────────────────────── */
  const loadFeedback = useCallback(async (status) => {
    setFeedbackLoading(true);
    setFeedbackError(null);
    try {
      const params = new URLSearchParams();
      params.set("limit", "200");
      if (status && status !== "all") params.set("status", status);
      const res = await fetch(`${API_BASE}/chat/feedback/list?${params.toString()}`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Server error: ${res.status}`);
      }
      const data = await res.json();
      setFeedback({
        items: Array.isArray(data.items) ? data.items : [],
        counts: data.counts || { learned: 0, unresolved: 0, no_comment: 0 },
        total: data.total ?? 0,
      });
    } catch (err) {
      setFeedbackError(err.message);
      setFeedback(null);
    } finally {
      setFeedbackLoading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/ai-config/meta`, {
          headers: { ...authHeaders() },
        });
        if (res.ok && alive) setMeta(await res.json());
      } catch {
        /* meta is optional; editors fall back to free-text */
      }
      try {
        const res = await fetch(`${API_BASE}/ai-config/defaults`, {
          headers: { ...authHeaders() },
        });
        if (res.ok && alive) setDefaults(await res.json());
      } catch {
        /* defaults are optional */
      }
      // Prompt defaults: the real built-in prompt text so the Prompts editor is
      // never empty (blank field = inherit the default shown here).
      try {
        const res = await fetch(`${API_BASE}/ai-config/prompt-defaults`, {
          headers: { ...authHeaders() },
        });
        if (res.ok) {
          if (alive) setPromptDefaults(await res.json());
        } else {
          const err = await res.json().catch(() => ({}));
          if (alive) setPromptDefaultsError(err.detail || err.message || `Server error: ${res.status}`);
        }
      } catch (err) {
        if (alive) setPromptDefaultsError(err.message || "Failed to load prompt defaults.");
      } finally {
        if (alive) setPromptDefaultsLoading(false);
      }
    })();
    loadList();
    loadOverview();
    return () => {
      alive = false;
    };
  }, [loadList, loadOverview]);

  // Lazy-load logs the first time the Logs tab is opened.
  useEffect(() => {
    if (tab === "logs") loadLogs();
  }, [tab, loadLogs]);

  // Lazy-load feedback the first time the Feedback tab is opened.
  useEffect(() => {
    if (tab === "feedback" && feedback === null && !feedbackLoading && !feedbackError) {
      loadFeedback(feedbackStatus);
    }
  }, [tab, feedback, feedbackLoading, feedbackError, feedbackStatus, loadFeedback]);

  // Re-fetch feedback when the status filter changes.
  const onFeedbackStatusChange = useCallback(
    (next) => {
      setFeedbackStatus(next);
      loadFeedback(next);
    },
    [loadFeedback]
  );

  /* ── Fetch: resolved preview for the chosen scope ──────────────────────── */
  const loadResolved = useCallback(async () => {
    setResolvedLoading(true);
    setResolvedError(null);
    try {
      const params = new URLSearchParams();
      if (scope.institution_id) params.set("institution_id", scope.institution_id);
      if (scope.app_id) params.set("app_id", scope.app_id);
      const qs = params.toString();
      const res = await fetch(`${API_BASE}/ai-config/resolved${qs ? `?${qs}` : ""}`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Server error: ${res.status}`);
      }
      setResolved(await res.json());
    } catch (err) {
      setResolvedError(err.message);
      setResolved(null);
    } finally {
      setResolvedLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    loadResolved();
  }, [loadResolved]);

  const providers = meta?.providers || [];
  const embeddingProviders = meta?.embedding_providers || [];
  const capabilities = meta?.capabilities || overview?.capabilities || [];
  const maskingModes = meta?.masking_modes || [
    "redact",
    "partial",
    "email",
    "hash",
    "initials",
    "none",
  ];
  const ruleEffects = meta?.rule_effects || ["deny", "allow"];

  /* ── Form field setters ────────────────────────────────────────────────── */
  const setField = (path, value) => {
    setForm((f) => {
      const next = { ...f };
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...cur[keys[i]] };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const setTierField = (tier_, key, value) => {
    setForm((f) => ({
      ...f,
      llm_tiers: {
        ...f.llm_tiers,
        [tier_]: { ...f.llm_tiers[tier_], [key]: value },
      },
    }));
  };

  /* ── Provider cascade ──────────────────────────────────────────────────── */
  const addProviderRow = () =>
    setForm((f) => ({ ...f, provider_priority: [...f.provider_priority, emptyProviderRow()] }));
  const removeProviderRow = (i) =>
    setForm((f) => ({
      ...f,
      provider_priority: f.provider_priority.filter((_, idx) => idx !== i),
    }));
  const setProviderField = (i, key, value) =>
    setForm((f) => ({
      ...f,
      provider_priority: f.provider_priority.map((r, idx) =>
        idx === i ? { ...r, [key]: value } : r
      ),
    }));
  const moveProviderRow = (i, dir) =>
    setForm((f) => {
      const arr = [...f.provider_priority];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, provider_priority: arr };
    });

  /* ── Capabilities ──────────────────────────────────────────────────────── */
  const capsAllMode = form.enabled_capabilities === null;
  const toggleCap = (key) => {
    setForm((f) => {
      const current = f.enabled_capabilities === null ? [] : [...f.enabled_capabilities];
      const idx = current.indexOf(key);
      if (idx === -1) current.push(key);
      else current.splice(idx, 1);
      return { ...f, enabled_capabilities: current };
    });
  };
  const setAllCaps = () => setForm((f) => ({ ...f, enabled_capabilities: null }));
  const clearCaps = () => setForm((f) => ({ ...f, enabled_capabilities: [] }));

  /* ── Security editor mutations ─────────────────────────────────────────── */
  const setSecurityField = (key, value) =>
    setForm((f) => ({ ...f, security: { ...f.security, [key]: value } }));

  /* ── Visitor (public admissions assistant) editor mutations ────────────── */
  const setVisitorField = (key, value) =>
    setForm((f) => ({ ...f, visitor: { ...f.visitor, [key]: value } }));
  const toggleVisitorSourceType = (type) =>
    setForm((f) => {
      const cur = Array.isArray(f.visitor.accessible_source_types)
        ? [...f.visitor.accessible_source_types]
        : [];
      const idx = cur.indexOf(type);
      if (idx === -1) cur.push(type);
      else cur.splice(idx, 1);
      return { ...f, visitor: { ...f.visitor, accessible_source_types: cur } };
    });

  const addMaskingRow = () =>
    setForm((f) => ({
      ...f,
      security: { ...f.security, masking: [...f.security.masking, emptyMaskingRow()] },
    }));
  const removeMaskingRow = (i) =>
    setForm((f) => ({
      ...f,
      security: {
        ...f.security,
        masking: f.security.masking.filter((_, idx) => idx !== i),
      },
    }));
  const setMaskingField = (i, key, value) =>
    setForm((f) => ({
      ...f,
      security: {
        ...f.security,
        masking: f.security.masking.map((r, idx) =>
          idx === i ? { ...r, [key]: value } : r
        ),
      },
    }));

  const addRuleRow = () =>
    setForm((f) => ({
      ...f,
      security: { ...f.security, rules: [...f.security.rules, emptyRuleRow()] },
    }));
  const removeRuleRow = (i) =>
    setForm((f) => ({
      ...f,
      security: { ...f.security, rules: f.security.rules.filter((_, idx) => idx !== i) },
    }));
  const setRuleField = (i, key, value) =>
    setForm((f) => ({
      ...f,
      security: {
        ...f.security,
        rules: f.security.rules.map((r, idx) =>
          idx === i ? { ...r, [key]: value } : r
        ),
      },
    }));

  // Hydrate the security editor from the effective (resolved) config the first
  // time the Security tab is opened, unless the user is editing a named config.
  const [securityHydrated, setSecurityHydrated] = useState(false);
  useEffect(() => {
    if (tab !== "security" || securityHydrated || form.id) return;
    const src = resolved?.security || defaults?.security || null;
    if (src) {
      setForm((f) => ({ ...f, security: securityFromRow(src) }));
      setSecurityHydrated(true);
    }
  }, [tab, securityHydrated, form.id, resolved, defaults]);

  // Hydrate the visitor editor from the effective (resolved) config the first
  // time the Visitor Access tab is opened, unless editing a named config.
  const [visitorHydrated, setVisitorHydrated] = useState(false);
  useEffect(() => {
    if (tab !== "visitor" || visitorHydrated || form.id) return;
    const src = resolved?.visitor || defaults?.visitor || null;
    if (src) {
      setForm((f) => ({ ...f, visitor: visitorFromRow(src) }));
      setVisitorHydrated(true);
    }
  }, [tab, visitorHydrated, form.id, resolved, defaults]);

  // Pre-fill the Prompts editors so they are never blank: for each slot use the
  // saved override (resolved config's prompts.<key>) when non-empty, otherwise
  // the built-in default text from /prompt-defaults. Only runs for a fresh draft
  // (not when editing a named config) and only once defaults have loaded.
  const buildEffectivePrompts = useCallback(() => {
    const defs = promptDefaults || {};
    const saved = resolved?.prompts || {};
    const keys = ["system", "medium", "minimal", "executive_addendum", "rag", "analytics", "tool_picker"];
    const out = {};
    keys.forEach((k) => {
      const override = saved[k];
      out[k] = override && String(override).trim() ? String(override) : String(defs[k] || "");
    });
    return out;
  }, [promptDefaults, resolved]);

  useEffect(() => {
    if (tab !== "prompts" || promptsHydrated || form.id || !promptDefaults) return;
    setForm((f) => ({ ...f, prompts: buildEffectivePrompts() }));
    setPromptsHydrated(true);
  }, [tab, promptsHydrated, form.id, promptDefaults, buildEffectivePrompts]);

  // Reset a single prompt field back to its built-in default.
  const resetPrompt = useCallback(
    (key) => {
      const def = String((promptDefaults || {})[key] || "");
      setField(`prompts.${key}`, def);
    },
    [promptDefaults]
  );

  /* ── Edit / new ────────────────────────────────────────────────────────── */
  const startNew = () => {
    setForm(blankForm());
    setPromptsHydrated(false); // re-pre-fill prompt editors for the fresh draft
    flash("info", "Started a new draft config.");
  };

  const editConfig = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/ai-config/${id}`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Server error: ${res.status}`);
      }
      const row = await res.json();
      const nextForm = formFromRow(row);
      // Pre-fill any prompt slot the config leaves blank with the built-in
      // default text so the editor always shows the real, editable prompt.
      const defs = promptDefaults || {};
      const keys = ["system", "medium", "minimal", "executive_addendum", "rag", "analytics", "tool_picker"];
      keys.forEach((k) => {
        if (!String(nextForm.prompts[k] || "").trim()) {
          nextForm.prompts[k] = String(defs[k] || "");
        }
      });
      setForm(nextForm);
      setPromptsHydrated(true); // form already carries the effective prompts
      setTab("configuration");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      flash("error", `Load failed: ${err.message}`);
    }
  };

  /* ── Save / Publish / Activate / Delete ────────────────────────────────── */
  const persist = async (publish) => {
    if (!form.name.trim()) {
      flash("error", "Name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload({ ...form, is_active: publish }, promptDefaults);
      const isUpdate = !!form.id;
      const url = isUpdate ? `${API_BASE}/ai-config/${form.id}` : `${API_BASE}/ai-config`;
      const res = await fetch(url, {
        method: isUpdate ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Server error: ${res.status}`);
      }
      const data = await res.json();
      const newId = data.id || form.id;
      if (newId) setForm((f) => ({ ...f, id: newId }));

      if (publish && newId) {
        const actRes = await fetch(`${API_BASE}/ai-config/${newId}/activate`, {
          method: "POST",
          headers: { ...authHeaders() },
        });
        if (!actRes.ok) {
          const err = await actRes.json().catch(() => ({}));
          throw new Error(err.detail || err.message || `Activation failed: ${actRes.status}`);
        }
        flash("success", "Published and activated.");
      } else {
        flash("success", "Draft saved.");
      }
      await loadList();
      await loadResolved();
      await loadOverview();
    } catch (err) {
      flash("error", `Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const activateConfig = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/ai-config/${id}/activate`, {
        method: "POST",
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Server error: ${res.status}`);
      }
      flash("success", "Config activated.");
      await loadList();
      await loadResolved();
      await loadOverview();
    } catch (err) {
      flash("error", `Activate failed: ${err.message}`);
    }
  };

  const deleteConfig = async (id) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm("Delete this configuration? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API_BASE}/ai-config/${id}`, {
        method: "DELETE",
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Server error: ${res.status}`);
      }
      flash("success", "Config deleted.");
      if (form.id === id) setForm(blankForm());
      await loadList();
      await loadResolved();
    } catch (err) {
      flash("error", `Delete failed: ${err.message}`);
    }
  };

  /* ── Test panel ────────────────────────────────────────────────────────── */
  const runTest = async () => {
    if (!testQ.trim() || testLoading) return;
    setTestLoading(true);
    setTestError(null);
    setTestResult(null);
    try {
      const res = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ question: testQ.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Server error: ${res.status}`);
      }
      setTestResult(await res.json());
    } catch (err) {
      setTestError(err.message);
    } finally {
      setTestLoading(false);
    }
  };

  /* ── Knowledge base actions ────────────────────────────────────────────── */
  const createSource = async (payload) => {
    const res = await fetch(`${API_BASE}/knowledge-sources`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || err.message || `Server error: ${res.status}`);
    }
    await loadSources();
    await loadDiscovered();
    return res.json();
  };

  const syncSource = async (id) => {
    const res = await fetch(`${API_BASE}/knowledge-sources/${id}/sync`, {
      method: "POST",
      headers: { ...authHeaders() },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || err.message || `Server error: ${res.status}`);
    }
    const data = await res.json();
    await loadSources();
    await loadDiscovered();
    return data;
  };

  const deleteSource = async (id) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm("Remove this knowledge source?")) return;
    try {
      const res = await fetch(`${API_BASE}/knowledge-sources/${id}`, {
        method: "DELETE",
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Server error: ${res.status}`);
      }
      flash("success", "Source removed.");
      await loadSources();
      await loadDiscovered();
    } catch (err) {
      flash("error", `Delete failed: ${err.message}`);
    }
  };

  const addDocument = async (payload) => {
    const res = await fetch(`${API_BASE}/knowledge/documents`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const e = new Error(err.detail || err.message || `Server error: ${res.status}`);
      e.status = res.status;
      throw e;
    }
    return res.json();
  };

  /* ── Render ────────────────────────────────────────────────────────────── */
  const scopeMode = scope.institution_id && scope.app_id
    ? "app"
    : scope.institution_id
    ? "institution"
    : "global";

  const activeMeta = TAB_META[tab] || { title: "", desc: "" };

  return (
    <div className="cfg-shell">
      {toast && <div className={`cfg-toast cfg-toast-${toast.type}`}>{toast.msg}</div>}

      {/* ── Left sidebar navigation ───────────────────────────────────────── */}
      <aside className="cfg-sidebar">
        <div className="cfg-sidebar-brand">
          <SiraguLogo size={28} />
          <div className="cfg-sidebar-brand-text">
            <span className="cfg-sidebar-name">Siragu</span>
            <span className="cfg-sidebar-tag">Control Center</span>
          </div>
        </div>

        <nav className="cfg-nav">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`cfg-nav-item ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              <span className="cfg-nav-icon"><TabIcon name={t.id} /></span>
              <span className="cfg-nav-label">{t.label}</span>
            </button>
          ))}
        </nav>

        <button className="cfg-back" onClick={onBack} type="button">
          &#8592; Back to Siragu
        </button>
      </aside>

      {/* ── Content area ──────────────────────────────────────────────────── */}
      <div className="cfg-content">
        <header className="cfg-pagehead">
          <div className="cfg-pagehead-text">
            <h1 className="cfg-pagehead-title">{activeMeta.title}</h1>
            <p className="cfg-pagehead-desc">{activeMeta.desc}</p>
          </div>
          <div className="cfg-pagehead-actions">
            {tab === "overview" && (
              <button className="cfg-btn cfg-btn-primary" onClick={loadOverview} type="button">
                Refresh
              </button>
            )}
            {tab === "configuration" && (
              <button className="cfg-btn cfg-btn-primary" onClick={startNew} type="button">
                + New configuration
              </button>
            )}
            {tab === "knowledge" && (
              <button className="cfg-btn cfg-btn-primary" onClick={() => { loadSources(); loadDiscovered(); }} type="button">
                Refresh
              </button>
            )}
          </div>
        </header>

        <div className="cfg-tabpanel">
        {tab === "overview" && (
          <OverviewTab
            overview={overview}
            loading={overviewLoading}
            error={overviewError}
            onRefresh={loadOverview}
          />
        )}

        {tab === "models" && (
          <ModelsTab
            form={form}
            providers={providers}
            setTierField={setTierField}
            setField={setField}
            persist={persist}
            saving={saving}
          />
        )}

        {tab === "tools" && (
          <ToolsTab
            capabilities={capabilities}
            capsAllMode={capsAllMode}
            form={form}
            toggleCap={toggleCap}
            setAllCaps={setAllCaps}
            clearCaps={clearCaps}
            persist={persist}
            saving={saving}
            discovered={discovered}
            discoveredLoading={discoveredLoading}
            discoveredError={discoveredError}
            onReloadDiscovered={loadDiscovered}
          />
        )}

        {tab === "prompts" && (
          <PromptsTab
            form={form}
            setField={setField}
            persist={persist}
            saving={saving}
            promptDefaults={promptDefaults}
            promptDefaultsLoading={promptDefaultsLoading}
            promptDefaultsError={promptDefaultsError}
            resetPrompt={resetPrompt}
          />
        )}

        {tab === "knowledge" && (
          <KnowledgeTab
            sources={sources}
            loading={sourcesLoading}
            error={sourcesError}
            discovered={discovered}
            discoveredLoading={discoveredLoading}
            onCreate={createSource}
            onSync={syncSource}
            onDelete={deleteSource}
            onAddDocument={addDocument}
            flash={flash}
          />
        )}

        {tab === "logs" && (
          <LogsTab logs={logs} loading={logsLoading} error={logsError} onRefresh={loadLogs} />
        )}

        {tab === "security" && (
          <SecurityTab
            overview={overview}
            form={form}
            maskingModes={maskingModes}
            ruleEffects={ruleEffects}
            capabilities={capabilities}
            setSecurityField={setSecurityField}
            addMaskingRow={addMaskingRow}
            removeMaskingRow={removeMaskingRow}
            setMaskingField={setMaskingField}
            addRuleRow={addRuleRow}
            removeRuleRow={removeRuleRow}
            setRuleField={setRuleField}
            persist={persist}
            saving={saving}
          />
        )}

        {tab === "visitor" && (
          <VisitorTab
            form={form}
            setVisitorField={setVisitorField}
            toggleVisitorSourceType={toggleVisitorSourceType}
            persist={persist}
            saving={saving}
          />
        )}

        {tab === "feedback" && (
          <FeedbackTab
            feedback={feedback}
            loading={feedbackLoading}
            error={feedbackError}
            status={feedbackStatus}
            onStatusChange={onFeedbackStatusChange}
            onRetry={() => loadFeedback(feedbackStatus)}
          />
        )}

        {tab === "configuration" && (
          <div className="cfg-layout">
            {/* ── Left column: scope preview + configs list ─────────────── */}
            <aside className="cfg-aside">
              {/* Scope selector */}
              <div className="cfg-card">
                <h2 className="cfg-card-title">Scope preview</h2>
                <div className="cfg-scope-tabs">
                  <button
                    type="button"
                    className={`cfg-tab ${scopeMode === "global" ? "active" : ""}`}
                    onClick={() => setScope({ institution_id: "", app_id: "" })}
                  >
                    Global
                  </button>
                  <button
                    type="button"
                    className={`cfg-tab ${scopeMode === "institution" ? "active" : ""}`}
                    onClick={() =>
                      setScope((s) => ({ institution_id: s.institution_id, app_id: "" }))
                    }
                  >
                    Institution
                  </button>
                  <button
                    type="button"
                    className={`cfg-tab ${scopeMode === "app" ? "active" : ""}`}
                    onClick={() => setScope((s) => ({ ...s }))}
                  >
                    Inst + App
                  </button>
                </div>

                <label className="cfg-label">Institution ID (UUID)</label>
                <input
                  className="cfg-input"
                  type="text"
                  placeholder="leave blank for global default"
                  value={scope.institution_id}
                  onChange={(e) =>
                    setScope((s) => ({ ...s, institution_id: e.target.value.trim() }))
                  }
                />
                <label className="cfg-label">App ID (UUID)</label>
                <input
                  className="cfg-input"
                  type="text"
                  placeholder="requires institution ID"
                  value={scope.app_id}
                  disabled={!scope.institution_id}
                  onChange={(e) => setScope((s) => ({ ...s, app_id: e.target.value.trim() }))}
                />

                <div className="cfg-preview">
                  {resolvedLoading && <div className="cfg-muted">Loading effective config…</div>}
                  {resolvedError && <div className="cfg-inline-error">{resolvedError}</div>}
                  {!resolvedLoading && !resolvedError && resolved && (
                    <ResolvedPreview resolved={resolved} defaults={defaults} />
                  )}
                </div>
              </div>

              {/* Configs list */}
              <div className="cfg-card">
                <div className="cfg-card-head-row">
                  <h2 className="cfg-card-title">Existing configs</h2>
                  <button className="cfg-btn cfg-btn-sm" onClick={loadList} type="button">
                    Refresh
                  </button>
                </div>

                {listLoading && <div className="cfg-muted">Loading configs…</div>}
                {listError && (
                  <div className="cfg-inline-error">
                    {listError}
                    <div className="cfg-muted cfg-mt">
                      The ai_config table may not exist yet. Save a config to create the first row.
                    </div>
                  </div>
                )}
                {!listLoading && !listError && configs.length === 0 && (
                  <div className="cfg-muted">No configs yet. Create one on the right.</div>
                )}
                {!listLoading && !listError && configs.length > 0 && (
                  <ul className="cfg-config-list">
                    {configs.map((c) => (
                      <li key={c.id} className="cfg-config-item">
                        <div className="cfg-config-main">
                          <div className="cfg-config-name">
                            {c.name}
                            {c.is_active && <span className="cfg-badge cfg-badge-active">Active</span>}
                          </div>
                          <div className="cfg-config-meta">
                            {scopeLabel(c)} · v{c.version ?? "—"}
                          </div>
                        </div>
                        <div className="cfg-config-actions">
                          <button className="cfg-btn cfg-btn-sm" onClick={() => editConfig(c.id)} type="button">
                            Edit
                          </button>
                          {!c.is_active && (
                            <button
                              className="cfg-btn cfg-btn-sm"
                              onClick={() => activateConfig(c.id)}
                              type="button"
                            >
                              Activate
                            </button>
                          )}
                          <button
                            className="cfg-btn cfg-btn-sm cfg-btn-danger"
                            onClick={() => deleteConfig(c.id)}
                            type="button"
                          >
                            Delete
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Test panel */}
              <div className="cfg-card">
                <h2 className="cfg-card-title">Test panel</h2>
                <HelperNote>Preview a chat answer with the live config before publishing.</HelperNote>
                <div className="cfg-test-row">
                  <input
                    className="cfg-input"
                    type="text"
                    placeholder="Ask a test question…"
                    value={testQ}
                    onChange={(e) => setTestQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && runTest()}
                  />
                  <button
                    className="cfg-btn cfg-btn-primary"
                    onClick={runTest}
                    disabled={testLoading || !testQ.trim()}
                    type="button"
                  >
                    {testLoading ? "Running…" : "Run test"}
                  </button>
                </div>
                {testError && <div className="cfg-inline-error cfg-mt">{testError}</div>}
                {testResult && (
                  <div className="cfg-test-result">
                    <div className="cfg-test-line">
                      <span className="cfg-test-key">Intent</span>
                      <span>{testResult.intent || "—"}</span>
                    </div>
                    <div className="cfg-test-line">
                      <span className="cfg-test-key">Rows</span>
                      <span>{testResult.row_count ?? 0}</span>
                    </div>
                    {testResult.message && (
                      <div className="cfg-test-msg">{testResult.message}</div>
                    )}
                  </div>
                )}
              </div>
            </aside>

            {/* ── Right column: editor ───────────────────────────────────── */}
            <main className="cfg-main">
              <div className="cfg-card">
                <div className="cfg-card-head-row">
                  <h2 className="cfg-card-title">
                    {form.id ? "Edit configuration" : "New configuration"}
                  </h2>
                  <button className="cfg-btn cfg-btn-sm" onClick={startNew} type="button">
                    + New
                  </button>
                </div>

                <div className="cfg-grid-2">
                  <div>
                    <label className="cfg-label">Name *</label>
                    <input
                      className="cfg-input"
                      type="text"
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      placeholder="e.g. Acme University production"
                    />
                  </div>
                  <div>
                    <label className="cfg-label">Description</label>
                    <input
                      className="cfg-input"
                      type="text"
                      value={form.description}
                      onChange={(e) => setField("description", e.target.value)}
                      placeholder="optional"
                    />
                  </div>
                  <div>
                    <label className="cfg-label">Institution ID (UUID)</label>
                    <input
                      className="cfg-input"
                      type="text"
                      value={form.institution_id}
                      onChange={(e) => setField("institution_id", e.target.value.trim())}
                      placeholder="blank = global default"
                    />
                  </div>
                  <div>
                    <label className="cfg-label">App ID (UUID)</label>
                    <input
                      className="cfg-input"
                      type="text"
                      value={form.app_id}
                      onChange={(e) => setField("app_id", e.target.value.trim())}
                      placeholder="blank = all apps"
                    />
                  </div>
                </div>
              </div>

              {/* LLM Tiers */}
              <Section
                id="tiers"
                title="LLM Tiers"
                subtitle="small · large · premium"
                open={openSections.tiers}
                onToggle={toggleSection}
              >
                <HelperNote>
                  Lightweight tier handles most traffic; escalates to large on low confidence; premium
                  is optional.
                </HelperNote>
                {["small", "large", "premium"].map((tier_) => (
                  <div key={tier_} className="cfg-tier">
                    <div className="cfg-tier-head">
                      <span className="cfg-tier-name">{tier_}</span>
                      <Toggle
                        checked={form.llm_tiers[tier_].enabled}
                        onChange={(v) => setTierField(tier_, "enabled", v)}
                        label="Enabled"
                      />
                    </div>
                    <div className="cfg-grid-2">
                      <div>
                        <label className="cfg-label">Provider</label>
                        <ProviderSelect
                          value={form.llm_tiers[tier_].provider}
                          options={providers}
                          onChange={(v) => setTierField(tier_, "provider", v)}
                        />
                      </div>
                      <div>
                        <label className="cfg-label">Model</label>
                        <input
                          className="cfg-input"
                          type="text"
                          value={form.llm_tiers[tier_].model}
                          onChange={(e) => setTierField(tier_, "model", e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="cfg-label">Endpoint (URL)</label>
                        <input
                          className="cfg-input"
                          type="text"
                          value={form.llm_tiers[tier_].endpoint}
                          onChange={(e) => setTierField(tier_, "endpoint", e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="cfg-label">
                          API key — Environment variable NAME (not the secret)
                        </label>
                        <input
                          className="cfg-input"
                          type="text"
                          value={form.llm_tiers[tier_].api_key_ref}
                          onChange={(e) => setTierField(tier_, "api_key_ref", e.target.value)}
                          placeholder="e.g. OPENAI_API_KEY"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </Section>

              {/* Providers cascade */}
              <Section
                id="providers"
                title="Providers cascade"
                subtitle="ordered fallback chain"
                open={openSections.providers}
                onToggle={toggleSection}
              >
                <HelperNote>Ordered from first to last; the top provider is tried first.</HelperNote>
                {form.provider_priority.length === 0 && (
                  <div className="cfg-muted">No providers. Add one to override the default chain.</div>
                )}
                {form.provider_priority.map((row, i) => (
                  <div key={i} className="cfg-provider-row">
                    <div className="cfg-provider-head">
                      <span className="cfg-provider-idx">#{i + 1}</span>
                      <div className="cfg-provider-order">
                        <button
                          className="cfg-btn cfg-btn-sm"
                          onClick={() => moveProviderRow(i, -1)}
                          disabled={i === 0}
                          type="button"
                          aria-label="Move up"
                        >
                          &#8593;
                        </button>
                        <button
                          className="cfg-btn cfg-btn-sm"
                          onClick={() => moveProviderRow(i, 1)}
                          disabled={i === form.provider_priority.length - 1}
                          type="button"
                          aria-label="Move down"
                        >
                          &#8595;
                        </button>
                        <button
                          className="cfg-btn cfg-btn-sm cfg-btn-danger"
                          onClick={() => removeProviderRow(i)}
                          type="button"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <div className="cfg-grid-2">
                      <div>
                        <label className="cfg-label">Provider</label>
                        <ProviderSelect
                          value={row.provider}
                          options={providers}
                          onChange={(v) => setProviderField(i, "provider", v)}
                        />
                      </div>
                      <div>
                        <label className="cfg-label">Models (comma-separated)</label>
                        <input
                          className="cfg-input"
                          type="text"
                          value={row.models}
                          onChange={(e) => setProviderField(i, "models", e.target.value)}
                          placeholder="gpt-4o, gpt-4o-mini"
                        />
                      </div>
                      <div>
                        <label className="cfg-label">Endpoint (URL)</label>
                        <input
                          className="cfg-input"
                          type="text"
                          value={row.endpoint}
                          onChange={(e) => setProviderField(i, "endpoint", e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="cfg-label">
                          Endpoint — Environment variable NAME (not the URL)
                        </label>
                        <input
                          className="cfg-input"
                          type="text"
                          value={row.endpoint_ref}
                          onChange={(e) => setProviderField(i, "endpoint_ref", e.target.value)}
                          placeholder="e.g. LLM_ENDPOINT"
                        />
                      </div>
                      <div>
                        <label className="cfg-label">
                          API key — Environment variable NAME (not the secret)
                        </label>
                        <input
                          className="cfg-input"
                          type="text"
                          value={row.api_key_ref}
                          onChange={(e) => setProviderField(i, "api_key_ref", e.target.value)}
                          placeholder="e.g. OPENAI_API_KEY"
                        />
                      </div>
                      <div>
                        <label className="cfg-label">Timeout (seconds)</label>
                        <input
                          className="cfg-input"
                          type="number"
                          value={row.timeout}
                          onChange={(e) => setProviderField(i, "timeout", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <button className="cfg-btn" onClick={addProviderRow} type="button">
                  + Add provider
                </button>
              </Section>

              {/* Prompts */}
              <Section
                id="prompts"
                title="Prompts"
                subtitle="leave blank to use default"
                open={openSections.prompts}
                onToggle={toggleSection}
              >
                {[
                  ["system", "System prompt"],
                  ["medium", "Medium prompt"],
                  ["minimal", "Minimal prompt"],
                  ["executive_addendum", "Executive addendum"],
                ].map(([key, label]) => (
                  <div key={key} className="cfg-field">
                    <label className="cfg-label">{label}</label>
                    <textarea
                      className="cfg-textarea"
                      rows={3}
                      value={form.prompts[key]}
                      onChange={(e) => setField(`prompts.${key}`, e.target.value)}
                      placeholder="leave blank to use default"
                    />
                  </div>
                ))}
              </Section>

              {/* Capabilities */}
              <Section
                id="capabilities"
                title="Capabilities (tools)"
                subtitle={capsAllMode ? "all enabled (null)" : `${form.enabled_capabilities.length} selected`}
                open={openSections.capabilities}
                onToggle={toggleSection}
              >
                <HelperNote>
                  Selecting all (or none-to-null) sends <code>null</code>, meaning every capability is
                  enabled.
                </HelperNote>
                <div className="cfg-caps-controls">
                  <button
                    className={`cfg-btn cfg-btn-sm ${capsAllMode ? "cfg-btn-primary" : ""}`}
                    onClick={setAllCaps}
                    type="button"
                  >
                    All (null)
                  </button>
                  <button className="cfg-btn cfg-btn-sm" onClick={clearCaps} type="button">
                    Select none
                  </button>
                </div>
                {capabilities.length === 0 && (
                  <div className="cfg-muted">No capability metadata available.</div>
                )}
                <div className="cfg-caps-grid">
                  {capabilities.map((cap) => {
                    const checked =
                      capsAllMode || form.enabled_capabilities.includes(cap.key);
                    return (
                      <label key={cap.key} className="cfg-cap">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCap(cap.key)}
                        />
                        <span className="cfg-cap-body">
                          <span className="cfg-cap-key">
                            {cap.key}
                            {cap.app && <span className="cfg-cap-app">{cap.app}</span>}
                          </span>
                          {cap.description && (
                            <span className="cfg-cap-desc">{cap.description}</span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </Section>

              {/* Guardrails */}
              <Section
                id="guardrails"
                title="Guardrails"
                open={openSections.guardrails}
                onToggle={toggleSection}
              >
                <div className="cfg-grid-2">
                  <div>
                    <label className="cfg-label">Max rows</label>
                    <input
                      className="cfg-input"
                      type="number"
                      value={form.guardrails.max_rows}
                      onChange={(e) => setField("guardrails.max_rows", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="cfg-label">Query timeout (seconds)</label>
                    <input
                      className="cfg-input"
                      type="number"
                      value={form.guardrails.query_timeout_seconds}
                      onChange={(e) => setField("guardrails.query_timeout_seconds", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="cfg-label">Temperature</label>
                    <input
                      className="cfg-input"
                      type="number"
                      step="0.1"
                      value={form.guardrails.temperature}
                      onChange={(e) => setField("guardrails.temperature", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="cfg-label">Max tokens</label>
                    <input
                      className="cfg-input"
                      type="number"
                      value={form.guardrails.max_tokens}
                      onChange={(e) => setField("guardrails.max_tokens", e.target.value)}
                    />
                  </div>
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Allowed schemas (comma-separated)</label>
                  <input
                    className="cfg-input"
                    type="text"
                    value={form.guardrails.allowed_schemas}
                    onChange={(e) => setField("guardrails.allowed_schemas", e.target.value)}
                    placeholder="public, analytics"
                  />
                </div>
              </Section>

              {/* Confidence */}
              <Section
                id="confidence"
                title="Confidence"
                open={openSections.confidence}
                onToggle={toggleSection}
              >
                <div className="cfg-field">
                  <label className="cfg-label">
                    Threshold ({Number(form.confidence.threshold).toFixed(2)})
                  </label>
                  <div className="cfg-slider-row">
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={form.confidence.threshold}
                      onChange={(e) => setField("confidence.threshold", Number(e.target.value))}
                      className="cfg-slider"
                    />
                    <input
                      className="cfg-input cfg-input-narrow"
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={form.confidence.threshold}
                      onChange={(e) => setField("confidence.threshold", Number(e.target.value))}
                    />
                  </div>
                </div>
                <Toggle
                  checked={form.confidence.premium_enabled}
                  onChange={(v) => setField("confidence.premium_enabled", v)}
                  label="Premium enabled"
                />
              </Section>

              {/* Embeddings */}
              <Section
                id="embeddings"
                title="Embeddings"
                subtitle="FAQ & document search"
                open={openSections.embeddings}
                onToggle={toggleSection}
              >
                <HelperNote>
                  Required for FAQ &amp; document search. Uses a hosted embeddings API (env-var name for
                  the key).
                </HelperNote>
                <Toggle
                  checked={form.embeddings.enabled}
                  onChange={(v) => setField("embeddings.enabled", v)}
                  label="Enabled"
                />
                <div className="cfg-grid-2 cfg-mt">
                  <div>
                    <label className="cfg-label">Provider</label>
                    <ProviderSelect
                      value={form.embeddings.provider}
                      options={embeddingProviders}
                      onChange={(v) => setField("embeddings.provider", v)}
                    />
                  </div>
                  <div>
                    <label className="cfg-label">Model</label>
                    <input
                      className="cfg-input"
                      type="text"
                      value={form.embeddings.model}
                      onChange={(e) => setField("embeddings.model", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="cfg-label">Endpoint (URL)</label>
                    <input
                      className="cfg-input"
                      type="text"
                      value={form.embeddings.endpoint}
                      onChange={(e) => setField("embeddings.endpoint", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="cfg-label">
                      Endpoint — Environment variable NAME (not the URL)
                    </label>
                    <input
                      className="cfg-input"
                      type="text"
                      value={form.embeddings.endpoint_ref}
                      onChange={(e) => setField("embeddings.endpoint_ref", e.target.value)}
                      placeholder="e.g. EMBEDDINGS_ENDPOINT"
                    />
                  </div>
                  <div>
                    <label className="cfg-label">
                      API key — Environment variable NAME (not the secret)
                    </label>
                    <input
                      className="cfg-input"
                      type="text"
                      value={form.embeddings.api_key_ref}
                      onChange={(e) => setField("embeddings.api_key_ref", e.target.value)}
                      placeholder="e.g. EMBEDDINGS_API_KEY"
                    />
                  </div>
                  <div>
                    <label className="cfg-label">Dimensions</label>
                    <input
                      className="cfg-input"
                      type="number"
                      value={form.embeddings.dimensions}
                      onChange={(e) => setField("embeddings.dimensions", e.target.value)}
                    />
                  </div>
                </div>
              </Section>

              {/* Features */}
              <Section
                id="features"
                title="Features"
                open={openSections.features}
                onToggle={toggleSection}
              >
                <div className="cfg-feature-toggles">
                  <Toggle
                    checked={form.features.faq_enabled}
                    onChange={(v) => setField("features.faq_enabled", v)}
                    label="FAQ enabled"
                  />
                  <Toggle
                    checked={form.features.rag_enabled}
                    onChange={(v) => setField("features.rag_enabled", v)}
                    label="RAG enabled"
                  />
                  <Toggle
                    checked={form.features.cache_enabled}
                    onChange={(v) => setField("features.cache_enabled", v)}
                    label="Cache enabled"
                  />
                </div>
                <div className="cfg-field cfg-mt">
                  <label className="cfg-label">Cache TTL (seconds)</label>
                  <input
                    className="cfg-input cfg-input-narrow"
                    type="number"
                    value={form.features.cache_ttl_seconds}
                    onChange={(e) => setField("features.cache_ttl_seconds", e.target.value)}
                  />
                </div>
              </Section>

              {/* Save bar */}
              <div className="cfg-savebar">
                <button
                  className="cfg-btn"
                  onClick={() => persist(false)}
                  disabled={saving}
                  type="button"
                >
                  {saving ? "Saving…" : "Save draft"}
                </button>
                <button
                  className="cfg-btn cfg-btn-primary"
                  onClick={() => persist(true)}
                  disabled={saving}
                  type="button"
                >
                  {saving ? "Publishing…" : "Publish & activate"}
                </button>
              </div>
            </main>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

/* ── Overview tab: dashboard ─────────────────────────────────────────────── */
function OverviewTab({ overview, loading, error, onRefresh }) {
  if (loading) return <div className="cfg-card cfg-muted">Loading dashboard…</div>;
  if (error)
    return (
      <div className="cfg-card">
        <div className="cfg-inline-error">{error}</div>
        <button className="cfg-btn cfg-btn-sm cfg-mt" onClick={onRefresh} type="button">
          Retry
        </button>
      </div>
    );
  if (!overview) return <div className="cfg-card cfg-muted">No overview data.</div>;

  const tiers = overview.tiers || {};
  const layers = overview.layers || [];
  const tierOrder = ["small", "large", "premium"];

  return (
    <div className="cfg-overview">
      {/* Hero */}
      <div className="cfg-card cfg-hero">
        <SiraguLogo size={44} />
        <div>
          <h2 className="cfg-hero-title">Siragu AI Engine</h2>
          <p className="cfg-hero-sub">
            Config source: <strong>{overview.config_source || "default"}</strong> ·
            {overview.premium_enabled ? " premium on" : " premium off"} ·
            embeddings {overview.embeddings_enabled ? "on" : "off"}
          </p>
        </div>
        <button className="cfg-btn cfg-btn-sm" onClick={onRefresh} type="button">
          Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="cfg-stat-grid">
        <StatCard label="Capabilities" value={overview.capability_count ?? 0} accent="cyan" />
        <StatCard
          label="Confidence threshold"
          value={overview.confidence_threshold != null ? Number(overview.confidence_threshold).toFixed(2) : "—"}
          accent="blue"
        />
        <StatCard label="Logged messages" value={overview.logged_messages ?? 0} accent="magenta" />
        <StatCard
          label="Embeddings"
          value={overview.embeddings_enabled ? "On" : "Off"}
          accent="orange"
        />
      </div>

      {/* AI pipeline */}
      {layers.length > 0 && (
        <div className="cfg-card">
          <h3 className="cfg-section-heading">AI Pipeline · {layers.length} layers</h3>
          <p className="cfg-muted cfg-mb">
            Each question flows through these layers. Only the shaded ones invoke an LLM.
          </p>
          <div className="cfg-pipeline">
            {layers.map((layer, i) => (
              <React.Fragment key={layer.n ?? i}>
                <div className={`cfg-layer ${layer.llm ? "is-llm" : "no-llm"}`}>
                  <div className="cfg-layer-num">{layer.n ?? i + 1}</div>
                  <div className="cfg-layer-body">
                    <div className="cfg-layer-name">{layer.name}</div>
                    {layer.detail && <div className="cfg-layer-detail">{layer.detail}</div>}
                  </div>
                  <span className={`cfg-layer-badge ${layer.llm ? "badge-llm" : "badge-nollm"}`}>
                    {layer.llm ? "LLM" : "no-LLM"}
                  </span>
                </div>
                {i < layers.length - 1 && <div className="cfg-layer-arrow">→</div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Model tiers */}
      <div className="cfg-card">
        <h3 className="cfg-section-heading">Active model tiers</h3>
        <div className="cfg-tier-cards">
          {tierOrder.map((k) => {
            const t = tiers[k] || {};
            const enabled = !!t.enabled;
            return (
              <div key={k} className={`cfg-tier-card ${enabled ? "on" : "off"}`}>
                <div className="cfg-tier-card-head">
                  <span className="cfg-tier-card-name">{k}</span>
                  <span className={`cfg-pill ${enabled ? "pill-on" : "pill-off"}`}>
                    {enabled ? "enabled" : "off"}
                  </span>
                </div>
                <div className="cfg-tier-card-model">{t.model || "—"}</div>
                <div className="cfg-tier-card-provider">{t.provider || "no provider"}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`cfg-stat cfg-stat-${accent}`}>
      <div className="cfg-stat-value">{value}</div>
      <div className="cfg-stat-label">{label}</div>
    </div>
  );
}

/* ── Models tab ──────────────────────────────────────────────────────────── */
function ModelsTab({ form, providers, setTierField, setField, persist, saving }) {
  return (
    <div className="cfg-tab-single">
      <div className="cfg-card">
        <h3 className="cfg-section-heading">LLM Tiers</h3>
        <HelperNote>
          The small tier handles most traffic and escalates to large on low confidence; premium is
          optional. Every API-key field is an <strong>environment-variable NAME</strong>, not the
          secret itself.
        </HelperNote>
        {["small", "large", "premium"].map((tier_) => (
          <div key={tier_} className="cfg-tier">
            <div className="cfg-tier-head">
              <span className="cfg-tier-name">{tier_}</span>
              <Toggle
                checked={form.llm_tiers[tier_].enabled}
                onChange={(v) => setTierField(tier_, "enabled", v)}
                label="Enabled"
              />
            </div>
            <div className="cfg-grid-2">
              <div>
                <label className="cfg-label">Provider</label>
                <ProviderSelect
                  value={form.llm_tiers[tier_].provider}
                  options={providers}
                  onChange={(v) => setTierField(tier_, "provider", v)}
                />
              </div>
              <div>
                <label className="cfg-label">Model</label>
                <input
                  className="cfg-input"
                  type="text"
                  value={form.llm_tiers[tier_].model}
                  onChange={(e) => setTierField(tier_, "model", e.target.value)}
                />
              </div>
              <div>
                <label className="cfg-label">Endpoint (URL)</label>
                <input
                  className="cfg-input"
                  type="text"
                  value={form.llm_tiers[tier_].endpoint}
                  onChange={(e) => setTierField(tier_, "endpoint", e.target.value)}
                />
              </div>
              <div>
                <label className="cfg-label">API key — env var NAME (not the secret)</label>
                <input
                  className="cfg-input"
                  type="text"
                  value={form.llm_tiers[tier_].api_key_ref}
                  onChange={(e) => setTierField(tier_, "api_key_ref", e.target.value)}
                  placeholder="e.g. OPENAI_API_KEY"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="cfg-card">
        <h3 className="cfg-section-heading">Confidence &amp; escalation</h3>
        <div className="cfg-field">
          <label className="cfg-label">
            Threshold ({Number(form.confidence.threshold).toFixed(2)})
          </label>
          <div className="cfg-slider-row">
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={form.confidence.threshold}
              onChange={(e) => setField("confidence.threshold", Number(e.target.value))}
              className="cfg-slider"
            />
            <input
              className="cfg-input cfg-input-narrow"
              type="number"
              min="0"
              max="1"
              step="0.01"
              value={form.confidence.threshold}
              onChange={(e) => setField("confidence.threshold", Number(e.target.value))}
            />
          </div>
        </div>
        <Toggle
          checked={form.confidence.premium_enabled}
          onChange={(v) => setField("confidence.premium_enabled", v)}
          label="Premium tier enabled"
        />
      </div>

      <SaveHint form={form} persist={persist} saving={saving} />
    </div>
  );
}

/* ── Tools tab ───────────────────────────────────────────────────────────── */
const TOOLS_PAGE_SIZE = 60;

function ToolsTab({
  capabilities,
  capsAllMode,
  form,
  toggleCap,
  setAllCaps,
  clearCaps,
  persist,
  saving,
  discovered,
  discoveredLoading,
  discoveredError,
  onReloadDiscovered,
}) {
  const apiTools = discovered?.api_tools || [];
  const apiToolCount = discovered?.api_tool_count ?? apiTools.length;

  const [query, setQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [visible, setVisible] = useState(TOOLS_PAGE_SIZE);

  // Distinct sources for the filter dropdown.
  const sources = React.useMemo(() => {
    const set = new Set();
    apiTools.forEach((t) => t.source && set.add(t.source));
    return Array.from(set).sort();
  }, [apiTools]);

  // Filter by search text + source (memoised; runs over ~2.7k rows only when
  // inputs change, and we render just a page of the result).
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return apiTools.filter((t) => {
      if (sourceFilter && t.source !== sourceFilter) return false;
      if (!q) return true;
      const hay = `${t.path || ""} ${t.summary || ""} ${t.source || ""} ${t.method || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [apiTools, query, sourceFilter]);

  // Reset the page size whenever the filter result changes.
  useEffect(() => {
    setVisible(TOOLS_PAGE_SIZE);
  }, [query, sourceFilter]);

  const shown = filtered.slice(0, visible);

  // Group the visible rows by source, preserving first-seen order.
  const shownGroups = React.useMemo(() => {
    const map = new Map();
    shown.forEach((t) => {
      const key = t.source || "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    });
    return Array.from(map.entries());
  }, [shown]);

  return (
    <div className="cfg-tab-single">
      {/* Governed capability catalog (enabled_capabilities editor) */}
      <div className="cfg-card">
        <div className="cfg-card-head-row">
          <h3 className="cfg-section-heading">Capability catalog</h3>
          <div className="cfg-caps-controls">
            <button
              className={`cfg-btn cfg-btn-sm ${capsAllMode ? "cfg-btn-primary" : ""}`}
              onClick={setAllCaps}
              type="button"
            >
              Enable all
            </button>
            <button className="cfg-btn cfg-btn-sm" onClick={clearCaps} type="button">
              Select none
            </button>
          </div>
        </div>
        <HelperNote>
          The governed tools Siragu is allowed to call to answer questions. "Enable all" sends{" "}
          <code>null</code> (every capability on).
        </HelperNote>
        {capabilities.length === 0 && (
          <div className="cfg-muted">No capability metadata available.</div>
        )}
        <div className="cfg-caps-grid">
          {capabilities.map((cap) => {
            const checked = capsAllMode || (form.enabled_capabilities || []).includes(cap.key);
            const perms = cap.required_perms || cap.requiredPerms || [];
            return (
              <label key={cap.key} className="cfg-cap">
                <input type="checkbox" checked={checked} onChange={() => toggleCap(cap.key)} />
                <span className="cfg-cap-body">
                  <span className="cfg-cap-key">
                    {cap.key}
                    {cap.app && <span className="cfg-cap-app">{cap.app}</span>}
                  </span>
                  {cap.description && <span className="cfg-cap-desc">{cap.description}</span>}
                  {Array.isArray(perms) && perms.length > 0 && (
                    <span className="cfg-cap-perms">
                      {perms.map((p) => (
                        <span key={p} className="cfg-perm-tag">{p}</span>
                      ))}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <SaveHint form={form} persist={persist} saving={saving} />

      {/* Discovered (learned) tools — read-only informational catalog */}
      <div className="cfg-card">
        <div className="cfg-card-head-row">
          <h3 className="cfg-section-heading">Discovered tools (learned)</h3>
          <div className="cfg-caps-controls">
            <span className="cfg-tools-stat">
              <strong>{apiToolCount.toLocaleString()}</strong> tools
            </span>
            <button
              className="cfg-btn cfg-btn-sm"
              onClick={onReloadDiscovered}
              type="button"
              disabled={discoveredLoading}
            >
              {discoveredLoading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>
        <HelperNote>
          Read-only. These are the API, GraphQL and repo operations Siragu has learned from your
          synced knowledge sources. They inform what the AI can do — no configuration needed here.
        </HelperNote>

        {discoveredError && (
          <div className="cfg-inline-error cfg-mt">
            {discoveredError}
            <div className="cfg-mt">
              <button className="cfg-btn cfg-btn-sm" onClick={onReloadDiscovered} type="button">
                Retry
              </button>
            </div>
          </div>
        )}

        {discoveredLoading && !discovered && (
          <div className="cfg-muted cfg-mt">Loading discovered tools…</div>
        )}

        {!discoveredLoading && !discoveredError && apiTools.length === 0 && (
          <div className="cfg-muted cfg-mt">
            No discovered tools yet. Add a Module API / repo source under Knowledge Base and press
            Sync.
          </div>
        )}

        {apiTools.length > 0 && (
          <>
            <div className="cfg-tools-controls">
              <input
                className="cfg-input cfg-tools-search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by path, summary or source…"
              />
              <select
                className="cfg-input cfg-tools-sourcesel"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <option value="">All sources ({sources.length})</option>
                {sources.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="cfg-tools-count cfg-muted">
              Showing {shown.length.toLocaleString()} of {filtered.length.toLocaleString()}
              {filtered.length !== apiTools.length && ` (filtered from ${apiTools.length.toLocaleString()})`}
            </div>

            {filtered.length === 0 ? (
              <div className="cfg-muted cfg-mt">No tools match your search.</div>
            ) : (
              <div className="cfg-tools-scroll">
                {shownGroups.map(([groupSource, rows]) => (
                  <div key={groupSource} className="cfg-tools-group">
                    <div className="cfg-tools-group-head">
                      {groupSource} <span className="cfg-tools-group-count">{rows.length}</span>
                    </div>
                    {rows.map((t, i) => {
                      const method = (t.method || "GET").toUpperCase();
                      const cls =
                        method === "QUERY" || method === "MUTATION"
                          ? "cfg-method-query"
                          : `cfg-method-${method.toLowerCase()}`;
                      return (
                        <div key={`${groupSource}-${t.path}-${i}`} className="cfg-tool-row">
                          <span className={`cfg-method ${cls}`}>{method}</span>
                          <span className="cfg-mono cfg-tool-path">{t.path}</span>
                          {t.summary && <span className="cfg-tool-summary">{t.summary}</span>}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            {visible < filtered.length && (
              <div className="cfg-tools-more">
                <button
                  className="cfg-btn cfg-btn-sm"
                  type="button"
                  onClick={() => setVisible((v) => v + TOOLS_PAGE_SIZE)}
                >
                  Show more ({(filtered.length - visible).toLocaleString()} remaining)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Prompts tab ─────────────────────────────────────────────────────────── */
const CORE_PROMPTS = [
  ["system", "System prompt", "The core instructions Siragu follows on every request.", 16],
  ["medium", "Medium prompt", "Used for mid-complexity answers.", 6],
  ["minimal", "Minimal prompt", "Used for the lightweight fast path.", 6],
  ["executive_addendum", "Executive addendum", "Extra guidance appended for executive summaries.", 5],
];
const BEHAVIOUR_PROMPTS = [
  ["rag", "RAG grounding prompt", "How Siragu answers from crawled sites and ingested code.", 8],
  ["analytics", "Analytics prompt", "How Siragu turns real data into recommendations.", 8],
  ["tool_picker", "Tool-picker prompt", "How Siragu chooses which module API to call.", 8],
];

function PromptField({ pkey, label, hint, rows, form, setField, promptDefaults, resetPrompt }) {
  const def = String((promptDefaults || {})[pkey] || "");
  const cur = String(form.prompts[pkey] || "");
  const isDefault = cur === def;
  return (
    <div className="cfg-prompt-field">
      <div className="cfg-prompt-head">
        <label className="cfg-label cfg-prompt-label">{label}</label>
        <div className="cfg-prompt-actions">
          <span className={`cfg-prompt-badge ${isDefault ? "is-default" : "is-override"}`}>
            {isDefault ? "Default" : "Overridden"}
          </span>
          <button
            type="button"
            className="cfg-link-btn"
            onClick={() => resetPrompt(pkey)}
            disabled={isDefault || !promptDefaults}
          >
            Reset to default
          </button>
        </div>
      </div>
      <div className="cfg-muted cfg-mb">{hint}</div>
      <textarea
        className="cfg-textarea cfg-textarea-mono"
        rows={rows}
        value={cur}
        onChange={(e) => setField(`prompts.${pkey}`, e.target.value)}
        placeholder="Prompt text…"
      />
    </div>
  );
}

function PromptsTab({
  form,
  setField,
  persist,
  saving,
  promptDefaults,
  promptDefaultsLoading,
  promptDefaultsError,
  resetPrompt,
}) {
  const fieldProps = { form, setField, promptDefaults, resetPrompt };
  return (
    <div className="cfg-tab-single">
      <div className="cfg-card">
        <h3 className="cfg-section-heading">Core prompts</h3>
        <HelperNote>
          Each editor is pre-filled with the <strong>effective prompt</strong> — your saved override,
          or the built-in default. Edit freely; a field left equal to the default is not stored, so
          it keeps tracking future default updates.
        </HelperNote>

        {promptDefaultsLoading && (
          <div className="cfg-muted cfg-mt">Loading default prompts…</div>
        )}
        {promptDefaultsError && (
          <div className="cfg-inline-error cfg-mt">
            Could not load default prompts: {promptDefaultsError}
          </div>
        )}

        {CORE_PROMPTS.map(([key, label, hint, rows]) => (
          <PromptField key={key} pkey={key} label={label} hint={hint} rows={rows} {...fieldProps} />
        ))}
      </div>

      <div className="cfg-card">
        <h3 className="cfg-section-heading">Behaviour prompts</h3>
        <HelperNote>
          Advanced prompts that steer specific pipelines — retrieval grounding, data analytics and
          tool selection.
        </HelperNote>
        {BEHAVIOUR_PROMPTS.map(([key, label, hint, rows]) => (
          <PromptField key={key} pkey={key} label={label} hint={hint} rows={rows} {...fieldProps} />
        ))}
      </div>

      <SaveHint form={form} persist={persist} saving={saving} />
    </div>
  );
}

/* Save hint used by the simple tabs. Publishing requires a named config in the
   Configuration tab; here we just offer Save draft for the active form. */
function SaveHint({ form, persist, saving }) {
  return (
    <div className="cfg-savebar">
      <span className="cfg-muted cfg-savehint">
        {form.name
          ? `Editing "${form.name}"`
          : "Tip: name & publish this config in the Configuration tab."}
      </span>
      <button
        className="cfg-btn"
        onClick={() => persist(false)}
        disabled={saving || !form.name}
        type="button"
        title={form.name ? "Save draft" : "Name the config in Configuration first"}
      >
        {saving ? "Saving…" : "Save draft"}
      </button>
      <button
        className="cfg-btn cfg-btn-primary"
        onClick={() => persist(true)}
        disabled={saving || !form.name}
        type="button"
      >
        {saving ? "Publishing…" : "Publish & activate"}
      </button>
    </div>
  );
}

/* ── Logs tab ────────────────────────────────────────────────────────────── */
function LogsTab({ logs, loading, error, onRefresh }) {
  return (
    <div className="cfg-tab-single">
      <div className="cfg-card">
        <div className="cfg-card-head-row">
          <h3 className="cfg-section-heading">Message logs</h3>
          <button className="cfg-btn cfg-btn-sm" onClick={onRefresh} type="button" disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
        <HelperNote>
          Logs populate once Identity Hub auth is enabled — each authenticated chat turn is recorded
          here with its intent, render type and any feedback.
        </HelperNote>
        {error && <div className="cfg-inline-error">{error}</div>}
        {!error && !loading && logs.length === 0 && (
          <div className="cfg-empty">
            <div className="cfg-empty-icon" aria-hidden="true">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 5h18M3 12h18M3 19h18" />
              </svg>
            </div>
            <div>No logs yet. They'll appear here once auth is live.</div>
          </div>
        )}
        {logs.length > 0 && (
          <div className="cfg-log-scroll">
            <table className="cfg-log-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Role</th>
                  <th>Intent</th>
                  <th>Message</th>
                  <th>Rows</th>
                  <th>Feedback</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className={l.is_error ? "row-error" : ""}>
                    <td className="cfg-log-time">{fmtTime(l.created_at)}</td>
                    <td>
                      <span className={`cfg-role-tag role-${l.role}`}>{l.role}</span>
                    </td>
                    <td>{l.intent || "—"}</td>
                    <td className="cfg-log-msg" title={l.message}>{l.message || "—"}</td>
                    <td>{l.row_count ?? "—"}</td>
                    <td>
                      {l.is_liked ? (
                        <span className="cfg-pill pill-on">up</span>
                      ) : l.is_disliked ? (
                        <span className="cfg-pill pill-off">down</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {l.is_error ? (
                        <span className="cfg-pill pill-off">error</span>
                      ) : (
                        <span className="cfg-pill pill-on">ok</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function fmtTime(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return String(ts);
  }
}

function fmtDate(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(ts);
  }
}

/* ── Feedback tab (disliked answers + learned corrections) ───────────────── */
const FEEDBACK_STATUS_META = {
  learned: { label: "Learned", cls: "cfg-feedback-badge-learned" },
  unresolved: { label: "Unresolved", cls: "cfg-feedback-badge-unresolved" },
  no_comment: { label: "No comment", cls: "cfg-feedback-badge-nocomment" },
};

function FeedbackTab({ feedback, loading, error, status, onStatusChange, onRetry }) {
  const items = feedback?.items || [];
  const counts = feedback?.counts || { learned: 0, unresolved: 0, no_comment: 0 };
  const total = feedback?.total ?? 0;

  return (
    <div className="cfg-tab-single">
      <div className="cfg-card">
        <div className="cfg-card-head-row">
          <h3 className="cfg-section-heading">Disliked answers</h3>
          <div className="cfg-feedback-toolbar">
            <label className="cfg-label cfg-feedback-filter-label" htmlFor="cfg-feedback-status">
              Status
            </label>
            <select
              id="cfg-feedback-status"
              className="cfg-input cfg-feedback-select"
              value={status}
              onChange={(e) => onStatusChange(e.target.value)}
            >
              <option value="all">All</option>
              <option value="learned">Learned</option>
              <option value="unresolved">Unresolved</option>
              <option value="no_comment">No comment</option>
            </select>
            <button
              className="cfg-btn cfg-btn-sm"
              onClick={onRetry}
              type="button"
              disabled={loading}
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>

        <HelperNote>
          Every &#128078; with a reason is turned into a learned correction the system applies to
          similar questions going forward.
        </HelperNote>

        <div className="cfg-feedback-stats">
          <div className="cfg-feedback-stat">
            <span className="cfg-feedback-stat-value">{total}</span>
            <span className="cfg-feedback-stat-label">Total disliked</span>
          </div>
          <div className="cfg-feedback-stat cfg-feedback-stat-learned">
            <span className="cfg-feedback-stat-value">{counts.learned ?? 0}</span>
            <span className="cfg-feedback-stat-label">Learned</span>
          </div>
          <div className="cfg-feedback-stat cfg-feedback-stat-unresolved">
            <span className="cfg-feedback-stat-value">{counts.unresolved ?? 0}</span>
            <span className="cfg-feedback-stat-label">Unresolved</span>
          </div>
        </div>

        {error && (
          <div className="cfg-inline-error cfg-feedback-error">
            {error}
            <button className="cfg-btn cfg-btn-sm cfg-mt" onClick={onRetry} type="button">
              Retry
            </button>
          </div>
        )}

        {loading && !error && <div className="cfg-muted cfg-mt">Loading feedback…</div>}

        {!loading && !error && items.length === 0 && (
          <div className="cfg-empty">
            <div className="cfg-empty-icon" aria-hidden="true">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 10v11" />
                <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88z" />
              </svg>
            </div>
            <div>No disliked answers for this filter.</div>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <ul className="cfg-feedback-list">
            {items.map((it) => {
              const meta = FEEDBACK_STATUS_META[it.status] || {
                label: it.status || "—",
                cls: "cfg-feedback-badge-nocomment",
              };
              return (
                <li key={it.id} className="cfg-feedback-item">
                  <div className="cfg-feedback-item-head">
                    <span className="cfg-feedback-question">{it.question || "—"}</span>
                    <span className={`cfg-feedback-badge ${meta.cls}`}>{meta.label}</span>
                  </div>
                  <div className="cfg-feedback-date">{fmtDate(it.created_at)}</div>
                  {it.comment && (
                    <div className="cfg-feedback-comment">{it.comment}</div>
                  )}
                  {it.guidance && (
                    <div className="cfg-feedback-fix">
                      <div className="cfg-feedback-fix-head">
                        <span className="cfg-feedback-fix-title">&#10003; Learned fix:</span>
                        {it.correction_type && (
                          <span className="cfg-feedback-fix-tag">{it.correction_type}</span>
                        )}
                        {it.hit_count != null && (
                          <span className="cfg-feedback-fix-applied">
                            applied {it.hit_count}&#215;
                          </span>
                        )}
                      </div>
                      <div className="cfg-feedback-fix-body">{it.guidance}</div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ── Security tab (editor) ───────────────────────────────────────────────── */
function SecurityTab({
  overview,
  form,
  maskingModes,
  ruleEffects,
  capabilities,
  setSecurityField,
  addMaskingRow,
  removeMaskingRow,
  setMaskingField,
  addRuleRow,
  removeRuleRow,
  setRuleField,
  persist,
  saving,
}) {
  const sec = form.security || emptySecurity();
  const enforced = overview?.security || [];
  const capKeys = (capabilities || []).map((c) => c.key).filter(Boolean);

  return (
    <div className="cfg-tab-single">
      {/* Hero */}
      <div className="cfg-card cfg-security-hero">
        <div className="cfg-security-shield" aria-hidden="true">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
        </div>
        <div>
          <h2 className="cfg-hero-title">Access, masking &amp; guardrails</h2>
          <p className="cfg-hero-sub">
            Define who counts as institution-tier, mask sensitive columns, and set allow/deny
            rules. No institutional data is sent to external LLMs — only schema names + the question.
          </p>
        </div>
      </div>

      {/* 1. Access boundary */}
      <div className="cfg-card">
        <h3 className="cfg-section-heading">Access boundary</h3>
        <HelperNote>
          Every permission and role below is a plain Identity Hub name / <code>resource:action</code>{" "}
          code — never a secret.
        </HelperNote>

        <div className="cfg-field">
          <label className="cfg-label">Institution read permissions</label>
          <div className="cfg-muted cfg-mb">
            Permissions that mark a user as institution-tier (e.g. <code>applicant:read</code>,{" "}
            <code>analytics:read</code>).
          </div>
          <TagInput
            value={sec.institution_read_perms}
            onChange={(v) => setSecurityField("institution_read_perms", v)}
            placeholder="applicant:read, analytics:read…"
          />
        </div>

        <div className="cfg-feature-toggles cfg-mt">
          <Toggle
            checked={sec.self_service_enabled}
            onChange={(v) => setSecurityField("self_service_enabled", v)}
            label="Self-service enabled"
          />
          <Toggle
            checked={sec.block_llm_sql}
            onChange={(v) => setSecurityField("block_llm_sql", v)}
            label="Governed-only mode — disable the raw LLM→SQL fallback"
          />
        </div>

        <div className="cfg-grid-2 cfg-mt">
          <div className="cfg-field">
            <label className="cfg-label">Extra sensitive columns</label>
            <div className="cfg-muted cfg-mb">Always treated as sensitive.</div>
            <TagInput
              value={sec.extra_sensitive_columns}
              onChange={(v) => setSecurityField("extra_sensitive_columns", v)}
              placeholder="phone, email, aadhaar…"
            />
          </div>
          <div className="cfg-field">
            <label className="cfg-label">Allow columns</label>
            <div className="cfg-muted cfg-mb">Explicitly exempted from masking.</div>
            <TagInput
              value={sec.allow_columns}
              onChange={(v) => setSecurityField("allow_columns", v)}
              placeholder="course_name, status…"
            />
          </div>
        </div>
      </div>

      {/* 2. Data masking rules */}
      <div className="cfg-card">
        <div className="cfg-card-head-row">
          <h3 className="cfg-section-heading">Data masking rules</h3>
          <button className="cfg-btn cfg-btn-sm cfg-btn-primary" onClick={addMaskingRow} type="button">
            + Add rule
          </button>
        </div>
        <HelperNote>
          Mask sensitive values in responses; excepted roles/permissions see the real value.
        </HelperNote>

        {sec.masking.length === 0 && (
          <div className="cfg-muted cfg-mt">No masking rules. Add one to redact sensitive columns.</div>
        )}

        {sec.masking.map((row, i) => (
          <div key={i} className="cfg-provider-row">
            <div className="cfg-provider-head">
              <span className="cfg-provider-idx">Rule #{i + 1}</span>
              <button
                className="cfg-btn cfg-btn-sm cfg-btn-danger"
                onClick={() => removeMaskingRow(i)}
                type="button"
              >
                Remove
              </button>
            </div>
            <div className="cfg-field">
              <label className="cfg-label">Columns</label>
              <TagInput
                value={row.columns}
                onChange={(v) => setMaskingField(i, "columns", v)}
                placeholder="email, phone…"
              />
            </div>
            <div className="cfg-grid-2">
              <div>
                <label className="cfg-label">Mode</label>
                <select
                  className="cfg-input"
                  value={row.mode}
                  onChange={(e) => setMaskingField(i, "mode", e.target.value)}
                >
                  {maskingModes.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="cfg-label">Show last {row.mode !== "partial" && "(partial only)"}</label>
                <input
                  className="cfg-input cfg-input-narrow"
                  type="number"
                  min="0"
                  value={row.show_last}
                  onChange={(e) => setMaskingField(i, "show_last", e.target.value)}
                  disabled={row.mode !== "partial"}
                  placeholder="4"
                />
              </div>
            </div>
            <div className="cfg-grid-2">
              <div className="cfg-field">
                <label className="cfg-label">Except roles</label>
                <TagInput
                  value={row.except_roles}
                  onChange={(v) => setMaskingField(i, "except_roles", v)}
                  placeholder="admin, registrar…"
                />
              </div>
              <div className="cfg-field">
                <label className="cfg-label">Except permissions</label>
                <TagInput
                  value={row.except_perms}
                  onChange={(v) => setMaskingField(i, "except_perms", v)}
                  placeholder="pii:read…"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 3. Access rules (allow / deny) */}
      <div className="cfg-card">
        <div className="cfg-card-head-row">
          <h3 className="cfg-section-heading">Access rules (allow / deny)</h3>
          <button className="cfg-btn cfg-btn-sm cfg-btn-primary" onClick={addRuleRow} type="button">
            + Add rule
          </button>
        </div>
        <HelperNote>
          Deny wins — a matching deny blocks the request even if the user has the permission.
        </HelperNote>

        {sec.rules.length === 0 && (
          <div className="cfg-muted cfg-mt">No access rules. Add one to allow or deny capabilities/topics.</div>
        )}

        {sec.rules.map((row, i) => (
          <div key={i} className="cfg-provider-row">
            <div className="cfg-provider-head">
              <span className="cfg-provider-idx">Rule #{i + 1}</span>
              <button
                className="cfg-btn cfg-btn-sm cfg-btn-danger"
                onClick={() => removeRuleRow(i)}
                type="button"
              >
                Remove
              </button>
            </div>
            <div className="cfg-grid-2">
              <div>
                <label className="cfg-label">Effect</label>
                <select
                  className="cfg-input"
                  value={row.effect}
                  onChange={(e) => setRuleField(i, "effect", e.target.value)}
                >
                  {ruleEffects.map((eff) => (
                    <option key={eff} value={eff}>{eff}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="cfg-label">When role</label>
                <input
                  className="cfg-input"
                  type="text"
                  value={row.when_role}
                  onChange={(e) => setRuleField(i, "when_role", e.target.value)}
                  placeholder="e.g. counsellor"
                />
              </div>
              <div>
                <label className="cfg-label">When permission</label>
                <input
                  className="cfg-input"
                  type="text"
                  value={row.when_permission}
                  onChange={(e) => setRuleField(i, "when_permission", e.target.value)}
                  placeholder="e.g. analytics:read"
                />
              </div>
              <div>
                <label className="cfg-label">Topics</label>
                <TagInput
                  value={row.topics}
                  onChange={(v) => setRuleField(i, "topics", v)}
                  placeholder="revenue, pii…"
                />
              </div>
            </div>
            <div className="cfg-field">
              <label className="cfg-label">Capabilities</label>
              <MultiSelectTags
                value={row.capabilities}
                options={capKeys}
                onChange={(v) => setRuleField(i, "capabilities", v)}
                placeholder="Select capabilities…"
              />
            </div>
          </div>
        ))}
      </div>

      <SaveHint form={form} persist={persist} saving={saving} />

      {/* 4. Always-on protections */}
      {enforced.length > 0 && (
        <div className="cfg-card">
          <h3 className="cfg-section-heading">Always-on protections</h3>
          <HelperNote>These safeguards are enforced by the engine and cannot be turned off.</HelperNote>
          <div className="cfg-security-grid cfg-mt">
            {enforced.map((s) => (
              <div key={s.key} className="cfg-security-card">
                <div className="cfg-security-check">✓</div>
                <div>
                  <div className="cfg-security-title">{s.title}</div>
                  {s.detail && <div className="cfg-security-detail">{s.detail}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Visitor Access tab (public admissions assistant) ────────────────────── */
const VISITOR_SOURCE_TYPES = [
  {
    value: "website",
    label: "Website",
    desc: "Public pages Siragu has crawled (course info, campus pages…).",
  },
  {
    value: "document",
    label: "Documents",
    desc: "Uploaded brochures, prospectuses and PDFs marked public.",
  },
];

function VisitorTab({ form, setVisitorField, toggleVisitorSourceType, persist, saving }) {
  const vis = form.visitor || emptyVisitor();
  const types = Array.isArray(vis.accessible_source_types)
    ? vis.accessible_source_types
    : [];

  return (
    <div className="cfg-tab-single">
      {/* Hero / explainer */}
      <div className="cfg-card cfg-visitor-hero">
        <div className="cfg-visitor-icon" aria-hidden="true">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21a8 8 0 0 1 16 0" />
          </svg>
        </div>
        <div>
          <h2 className="cfg-hero-title">Public visitor assistant</h2>
          <p className="cfg-hero-sub">
            Visitors (not signed in) get a warm admissions assistant answering ONLY from public
            website/documents — never internal data or code.
          </p>
        </div>
      </div>

      {/* Enable toggle */}
      <div className="cfg-card">
        <h3 className="cfg-section-heading">Availability</h3>
        <div className="cfg-feature-toggles">
          <Toggle
            checked={vis.enabled}
            onChange={(v) => setVisitorField("enabled", v)}
            label="Enable public visitor assistant"
          />
        </div>
        <HelperNote>
          When off, unauthenticated visitors are asked to sign in instead of chatting.
        </HelperNote>
      </div>

      {/* Accessible public sources */}
      <div className="cfg-card">
        <h3 className="cfg-section-heading">Accessible public sources</h3>
        <HelperNote>
          Which public source types the visitor assistant may draw on. Code and internal data are
          never available to visitors.
        </HelperNote>
        <div className="cfg-visitor-sources">
          {VISITOR_SOURCE_TYPES.map((t) => {
            const checked = types.includes(t.value);
            return (
              <label
                key={t.value}
                className={`cfg-visitor-source ${checked ? "on" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleVisitorSourceType(t.value)}
                />
                <span className="cfg-visitor-source-body">
                  <span className="cfg-visitor-source-label">{t.label}</span>
                  <span className="cfg-visitor-source-desc">{t.desc}</span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Highlights */}
      <div className="cfg-card">
        <h3 className="cfg-section-heading">Highlights to emphasize</h3>
        <HelperNote>
          Real facts (placements, rankings, recruiters, facilities…) woven into answers. These must
          be true — the assistant will present them as fact.
        </HelperNote>
        <textarea
          className="cfg-textarea"
          rows={6}
          value={vis.highlights || ""}
          onChange={(e) => setVisitorField("highlights", e.target.value)}
          placeholder="e.g. 95% placement rate in 2025; ranked top 10 in the region; recruiters include Acme, Globex; new AI research lab…"
        />
      </div>

      {/* Persona prompt */}
      <div className="cfg-card">
        <h3 className="cfg-section-heading">Admissions persona</h3>
        <HelperNote>
          Tone and voice for the admissions counselor. Leave blank to use the built-in warm
          admissions persona.
        </HelperNote>
        <textarea
          className="cfg-textarea cfg-textarea-mono"
          rows={8}
          value={vis.persona_prompt || ""}
          onChange={(e) => setVisitorField("persona_prompt", e.target.value)}
          placeholder="Blank = built-in warm admissions counselor. Override to set a custom tone, e.g. 'You are a friendly, encouraging admissions counselor. Be concise, welcoming, and never pushy…'"
        />
      </div>

      <SaveHint form={form} persist={persist} saving={saving} />
    </div>
  );
}

/* Multi-select rendered as a checklist of chips (no external deps). Falls back
   to a free-text tag input when no options are supplied. */
function MultiSelectTags({ value, options, onChange, placeholder }) {
  const selected = Array.isArray(value) ? value : [];
  if (!options || options.length === 0) {
    return <TagInput value={selected} onChange={onChange} placeholder={placeholder} />;
  }
  const toggle = (opt) => {
    if (selected.includes(opt)) onChange(selected.filter((x) => x !== opt));
    else onChange([...selected, opt]);
  };
  return (
    <div className="cfg-multiselect">
      {options.map((opt) => (
        <label
          key={opt}
          className={`cfg-chip-toggle ${selected.includes(opt) ? "on" : ""}`}
        >
          <input
            type="checkbox"
            checked={selected.includes(opt)}
            onChange={() => toggle(opt)}
          />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );
}

/* ── Knowledge Base tab ──────────────────────────────────────────────────── */
const SOURCE_TYPES = [
  { value: "api", label: "Module API (OpenAPI)" },
  { value: "git", label: "Git repo (source code)" },
  { value: "website", label: "Website" },
  { value: "database", label: "Database" },
];

function statusTone(status) {
  const s = (status || "").toLowerCase();
  if (["ok", "ready", "synced", "active", "success"].includes(s)) return "ok";
  if (["error", "failed", "failure"].includes(s)) return "error";
  return "pending";
}

function KnowledgeTab({
  sources,
  loading,
  error,
  discovered,
  discoveredLoading,
  onCreate,
  onSync,
  onDelete,
  onAddDocument,
  flash,
}) {
  // Add-source form
  const [sType, setSType] = useState("api");
  const [sName, setSName] = useState("");
  const [sUrl, setSUrl] = useState("");
  const [creating, setCreating] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  // Website quick-add
  const [webName, setWebName] = useState("");
  const [webUrl, setWebUrl] = useState("");
  const [addingWeb, setAddingWeb] = useState(false);

  // Document form
  const [docTitle, setDocTitle] = useState("");
  const [docContent, setDocContent] = useState("");
  const [docSubmitting, setDocSubmitting] = useState(false);
  const [embeddingsBanner, setEmbeddingsBanner] = useState(false);

  const submitSource = async (e) => {
    e.preventDefault();
    if (!sName.trim()) {
      flash("error", "Source name is required.");
      return;
    }
    setCreating(true);
    try {
      await onCreate({
        source_type: sType,
        name: sName.trim(),
        url: sUrl.trim() || null,
        enabled: true,
      });
      flash("success", "Source added.");
      setSName("");
      setSUrl("");
    } catch (err) {
      flash("error", `Add failed: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  const submitWebsite = async (e) => {
    e.preventDefault();
    if (!webUrl.trim()) {
      flash("error", "Website URL is required.");
      return;
    }
    setAddingWeb(true);
    try {
      await onCreate({
        source_type: "website",
        name: webName.trim() || webUrl.trim(),
        url: webUrl.trim(),
        enabled: true,
      });
      flash("success", "Website added as a knowledge source.");
      setWebName("");
      setWebUrl("");
    } catch (err) {
      flash("error", `Add failed: ${err.message}`);
    } finally {
      setAddingWeb(false);
    }
  };

  const submitDocument = async (e) => {
    e.preventDefault();
    if (!docTitle.trim() || !docContent.trim()) {
      flash("error", "Document title and content are required.");
      return;
    }
    setDocSubmitting(true);
    try {
      await onAddDocument({ title: docTitle.trim(), content: docContent.trim() });
      flash("success", "Document ingested.");
      setDocTitle("");
      setDocContent("");
      setEmbeddingsBanner(false);
    } catch (err) {
      if (err.status === 400 && /embedding/i.test(err.message || "")) {
        setEmbeddingsBanner(true);
      } else {
        flash("error", `Ingestion failed: ${err.message}`);
      }
    } finally {
      setDocSubmitting(false);
    }
  };

  const onFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setDocContent(String(reader.result || ""));
      if (!docTitle.trim()) setDocTitle(file.name.replace(/\.[^.]+$/, ""));
    };
    reader.readAsText(file);
  };

  const runSync = async (id) => {
    setSyncingId(id);
    try {
      const data = await onSync(id);
      flash("success", `Synced — ${data.discovered_count ?? 0} item(s) discovered.`);
    } catch (err) {
      flash("error", `Sync failed: ${err.message}`);
    } finally {
      setSyncingId(null);
    }
  };

  const apiTools = discovered?.api_tools || [];

  return (
    <div className="cfg-tab-single">
      {/* Sources table */}
      <div className="cfg-card">
        <div className="cfg-card-head-row">
          <h3 className="cfg-section-heading">Sources</h3>
          <span className="cfg-muted">{sources.length} configured</span>
        </div>
        <HelperNote>
          Point at a module's OpenAPI URL (e.g. <code>http://localhost:8000/openapi.json</code>) to
          learn its APIs as tools.
        </HelperNote>

        {error && <div className="cfg-inline-error cfg-mt">{error}</div>}
        {loading && <div className="cfg-muted cfg-mt">Loading sources…</div>}

        {!loading && !error && sources.length === 0 && (
          <div className="cfg-empty">
            <div>No knowledge sources yet. Add one below.</div>
          </div>
        )}

        {sources.length > 0 && (
          <div className="cfg-table-scroll">
            <table className="cfg-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>URL</th>
                  <th>Status</th>
                  <th className="cfg-num">Discovered</th>
                  <th>Last synced</th>
                  <th className="cfg-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((s) => (
                  <tr key={s.id}>
                    <td><span className="cfg-type-pill">{s.source_type}</span></td>
                    <td className="cfg-strong">{s.name}</td>
                    <td className="cfg-mono cfg-truncate" title={s.url}>{s.url || "—"}</td>
                    <td>
                      <span className={`cfg-status cfg-status-${statusTone(s.status)}`}>
                        {s.status || "pending"}
                      </span>
                    </td>
                    <td className="cfg-num">{s.discovered_count ?? 0}</td>
                    <td className="cfg-log-time">{fmtTime(s.last_synced_at)}</td>
                    <td className="cfg-actions-col">
                      <button
                        className="cfg-btn cfg-btn-sm"
                        onClick={() => runSync(s.id)}
                        disabled={syncingId === s.id}
                        type="button"
                      >
                        {syncingId === s.id ? (
                          <span className="cfg-spinner" aria-hidden="true" />
                        ) : (
                          "Sync"
                        )}
                      </button>
                      <button
                        className="cfg-btn cfg-btn-sm cfg-btn-danger"
                        onClick={() => onDelete(s.id)}
                        type="button"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add source form */}
        <form className="cfg-kb-form" onSubmit={submitSource}>
          <div className="cfg-grid-3">
            <div>
              <label className="cfg-label">Type</label>
              <select className="cfg-input" value={sType} onChange={(e) => setSType(e.target.value)}>
                {SOURCE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="cfg-label">Name</label>
              <input
                className="cfg-input"
                type="text"
                value={sName}
                onChange={(e) => setSName(e.target.value)}
                placeholder="e.g. Admissions module"
              />
            </div>
            <div>
              <label className="cfg-label">URL</label>
              <input
                className="cfg-input"
                type="text"
                value={sUrl}
                onChange={(e) => setSUrl(e.target.value)}
                placeholder="http://localhost:8000/openapi.json"
              />
            </div>
          </div>
          <div className="cfg-kb-form-actions">
            <button className="cfg-btn cfg-btn-primary" type="submit" disabled={creating}>
              {creating ? "Adding…" : "Add source"}
            </button>
          </div>
        </form>
      </div>

      {/* Website & Documents */}
      <div className="cfg-card">
        <h3 className="cfg-section-heading">Website &amp; Documents</h3>
        <HelperNote>
          Give Siragu a website URL or upload documents to use as a knowledge base.
        </HelperNote>

        {embeddingsBanner && (
          <div className="cfg-banner cfg-banner-info">
            <strong>Embeddings not configured.</strong> Document &amp; FAQ ingestion needs an
            embeddings provider. Enable it under <em>Configuration → Embeddings</em>, then retry.
          </div>
        )}

        <div className="cfg-kb-split">
          {/* Website */}
          <form className="cfg-kb-col" onSubmit={submitWebsite}>
            <h4 className="cfg-subhead">Add a website</h4>
            <label className="cfg-label">Name (optional)</label>
            <input
              className="cfg-input"
              type="text"
              value={webName}
              onChange={(e) => setWebName(e.target.value)}
              placeholder="Institution website"
            />
            <label className="cfg-label">Website URL</label>
            <input
              className="cfg-input"
              type="text"
              value={webUrl}
              onChange={(e) => setWebUrl(e.target.value)}
              placeholder="https://example.edu"
            />
            <div className="cfg-kb-form-actions">
              <button className="cfg-btn cfg-btn-primary" type="submit" disabled={addingWeb}>
                {addingWeb ? "Adding…" : "Add website"}
              </button>
            </div>
          </form>

          {/* Document */}
          <form className="cfg-kb-col" onSubmit={submitDocument}>
            <h4 className="cfg-subhead">Ingest a document</h4>
            <label className="cfg-label">Title</label>
            <input
              className="cfg-input"
              type="text"
              value={docTitle}
              onChange={(e) => setDocTitle(e.target.value)}
              placeholder="Admissions policy 2026"
            />
            <label className="cfg-label">Content</label>
            <textarea
              className="cfg-textarea"
              rows={5}
              value={docContent}
              onChange={(e) => setDocContent(e.target.value)}
              placeholder="Paste text, or read a .txt/.md file below…"
            />
            <label className="cfg-label">Or read from a text file</label>
            <input
              className="cfg-input cfg-file"
              type="file"
              accept=".txt,.md,.csv,.json,text/*"
              onChange={(e) => onFile(e.target.files && e.target.files[0])}
            />
            <div className="cfg-kb-form-actions">
              <button className="cfg-btn cfg-btn-primary" type="submit" disabled={docSubmitting}>
                {docSubmitting ? "Ingesting…" : "Ingest document"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Discovered-tool summary (full catalog with search lives in the Tools tab) */}
      <div className="cfg-card">
        <div className="cfg-card-head-row">
          <h3 className="cfg-section-heading">Discovered tools</h3>
          {discovered && (
            <span className="cfg-muted">
              {discovered.capability_count ?? 0} capabilities · {discovered.api_tool_count ?? 0} API tools
            </span>
          )}
        </div>
        <HelperNote>
          Siragu has learned{" "}
          <strong>{discovered ? (discovered.api_tool_count ?? 0).toLocaleString() : "…"}</strong>{" "}
          callable operations from your synced sources. Browse and search the full catalog under the{" "}
          <strong>Tools</strong> tab.
        </HelperNote>
        {discoveredLoading && <div className="cfg-muted cfg-mt">Loading discovered tools…</div>}
        {!discoveredLoading && apiTools.length === 0 && (
          <div className="cfg-muted cfg-mt">
            No API tools discovered yet. Add a Module API source and press Sync.
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Provider dropdown that also accepts a free-text value ───────────────── */
function ProviderSelect({ value, options, onChange }) {
  // Normalise option shape: strings or {key/name/value}
  const opts = (options || []).map((o) =>
    typeof o === "string" ? o : o.key || o.name || o.value || o.provider || String(o)
  );
  const known = opts.includes(value);
  if (opts.length === 0) {
    return (
      <input
        className="cfg-input"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="provider name"
      />
    );
  }
  return (
    <select className="cfg-input" value={known ? value : ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">— none —</option>
      {opts.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
      {value && !known && (
        <option value={value}>{value} (custom)</option>
      )}
    </select>
  );
}

/* ── Effective-config preview panel ──────────────────────────────────────── */
function ResolvedPreview({ resolved, defaults }) {
  const r = resolved || {};
  const d = defaults || {};
  const g = r.guardrails || d.guardrails || {};
  const conf = r.confidence || d.confidence || {};
  const emb = r.embeddings || d.embeddings || {};
  const feat = r.features || d.features || {};
  const tiers = r.llm_tiers || d.llm_tiers || {};
  const caps = r.enabled_capabilities;

  const row = (k, v) => (
    <div className="cfg-preview-row">
      <span className="cfg-preview-key">{k}</span>
      <span className="cfg-preview-val">{v}</span>
    </div>
  );

  return (
    <div className="cfg-preview-body">
      {row("Source", r.source || "default")}
      {row(
        "Tiers",
        ["small", "large", "premium"]
          .filter((t) => tiers[t] && tiers[t].enabled)
          .join(", ") || "—"
      )}
      {row(
        "Capabilities",
        caps === null || caps === undefined ? "all" : `${caps.length} selected`
      )}
      {row("Confidence", conf.threshold != null ? Number(conf.threshold).toFixed(2) : "—")}
      {row(
        "Embeddings",
        emb.enabled ? `${emb.provider || "?"} / ${emb.model || "?"}` : "off"
      )}
      {row(
        "Features",
        [
          feat.faq_enabled && "FAQ",
          feat.rag_enabled && "RAG",
          feat.cache_enabled && "Cache",
        ]
          .filter(Boolean)
          .join(", ") || "—"
      )}
      {row("Max rows", g.max_rows != null ? g.max_rows : "—")}
    </div>
  );
}

export default AiConfigPage;
